import { AuthService } from './authService';
import { getAuthService } from './authService';
import { Volunteer, VolunteerStatus, ClinicSchedule, ClinicStatus, ActivityLog, ActivityType, Comment } from '../types/sharepoint';
import { spfi } from '@pnp/sp';

// Use environment variable with fallback to the correct URL from your SharePoint site
const SHAREPOINT_SITE_URL = process.env.REACT_APP_SHAREPOINT_SITE_URL || 'https://netorgft17318468.sharepoint.com/sites/TrueCareClinicVolunteers';

// Initialize PnPjs
export const initializeSP = (siteUrl: string) => {
  const sp = spfi(siteUrl);
  return sp;
};

// Types for SharePoint data
export interface PrayerItem {
  Id: number;
  Title: string;
  PrayerText: string;
  Created: string;
  CreatedBy: string;
  AssignedTo?: string;
  CommentCount?: number;
}

export interface PrayerComment {
  Id: number;
  CommentText: string;
  Created: string;
  CreatedBy: {
    Title: string;
    Id: number;
  };
}

export interface ItemAddResult {
  data: any;
  item: any;
}

export class SharePointService {
  private authService: AuthService;
  private initialized: boolean = false;
  private lastRequestTime: number = 0;
  private requestCount: number = 0;
  private readonly MAX_REQUESTS_PER_MINUTE = 60;
  
  // Caching for performance
  private siteIdCache: string | null = null;
  private schemaCache: any = null;
  private volunteersCache: Volunteer[] | null = null;
  private commentCountsCache: Record<string, number> | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor(authService: AuthService) {
    this.authService = authService;
  }

  // Initialize SharePoint lists
  async initialize(): Promise<void> {
    if (this.initialized) return;
    
    try {
      // Skip list creation - work with existing lists only
      console.log('ℹ️ SharePoint service initialized (using existing lists only)');
      this.initialized = true;
      console.log('✅ SharePoint service initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize SharePoint service:', error);
      // Don't throw - allow the app to continue with mock data
    }
  }

  private async getGraphApiHeaders(): Promise<Headers> {
    const token = await this.authService.getAccessToken();
    if (!token) {
      throw new Error('No access token available. Please sign in first.');
    }

    return new Headers({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  async makeGraphRequest(endpoint: string, options: RequestInit = {}, allowNonIndexedQueries: boolean = false): Promise<any> {
    try {
      // Rate limiting - only if we're hitting limits
      const now = Date.now();
      if (this.requestCount >= this.MAX_REQUESTS_PER_MINUTE) {
        console.warn('⚠️ Rate limit approaching, waiting before next request...');
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
        this.requestCount = 0;
      }
      
      this.lastRequestTime = now;
      this.requestCount++;
      
      const headers = await this.getGraphApiHeaders();
      const url = `https://graph.microsoft.com/v1.0${endpoint}`;
      
      // Convert Headers object to plain object for proper merging
      const headerObject: Record<string, string> = {};
      headers.forEach((value, key) => {
        headerObject[key] = value;
      });
      
      // Add Prefer header for non-indexed queries if needed
      if (allowNonIndexedQueries) {
        headerObject['Prefer'] = 'HonorNonIndexedQueriesWarningMayFailRandomly';
      }
      
      const response = await fetch(url, {
        ...options,
        headers: {
          ...headerObject,
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Graph API error: ${response.status}`, errorText);
        
        // Handle rate limiting specifically
        if (response.status === 429) {
          console.warn('⚠️ Rate limited by Microsoft Graph API, waiting before retry...');
          await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds
          throw new Error(`Graph API request failed: ${response.status} - Rate limited`);
        }
        
        throw new Error(`Graph API request failed: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Graph API request failed:', error);
      throw error;
    }
  }

  // Get site ID with caching
  async getSiteId(): Promise<string> {
    // Return cached site ID if available
    if (this.siteIdCache) {
      return this.siteIdCache;
    }

    try {
      // Extract the hostname and site path from the URL
      const url = new URL(SHAREPOINT_SITE_URL);
      const hostname = url.hostname;
      const sitePath = url.pathname.replace('/sites/', '');
      
      // Try the correct format: /sites/{hostname}:/sites/{site-name}
      const response = await this.makeGraphRequest(`/sites/${hostname}:/sites/${sitePath}`);
      this.siteIdCache = response.id;
      return response.id;
    } catch (error) {
      console.log('Failed to get site by URL, trying alternative formats...');
      
      try {
        // Try with just the site name
        const url = new URL(SHAREPOINT_SITE_URL);
        const sitePath = url.pathname.replace('/sites/', '');
        const response = await this.makeGraphRequest(`/sites/${sitePath}`);
        this.siteIdCache = response.id;
        return response.id;
      } catch (error2) {
        console.log('Failed to get site by site name, trying root site...');
        try {
          // Try the tenant root site
          const rootResponse = await this.makeGraphRequest('/sites/root');
          this.siteIdCache = rootResponse.id;
          return rootResponse.id;
        } catch (rootError) {
          console.log('Failed to get root site, trying to list all sites...');
          // List all sites to find the correct one
          const sitesResponse = await this.makeGraphRequest('/sites?$select=id,name,webUrl,displayName');
          if (sitesResponse.value && sitesResponse.value.length > 0) {
            this.siteIdCache = sitesResponse.value[0].id;
            return sitesResponse.value[0].id;
          }
          throw new Error('No SharePoint sites found');
        }
      }
    }
  }

  // Get volunteer list schema with caching
  private async getVolunteerListSchema(): Promise<any> {
    if (this.schemaCache) {
      return this.schemaCache;
    }

    try {
      const siteId = await this.getSiteId();
      const response = await this.makeGraphRequest(
        `/sites/${siteId}/lists/True Care Clinic Volunteer List/columns`
      );
      this.schemaCache = response.value;
      return response.value;
    } catch (error) {
      console.error('Error fetching volunteer list schema:', error);
      throw error;
    }
  }

  // Optimized volunteer fetching with caching and selective fields
  async getVolunteers(): Promise<Volunteer[]> {
    // Check cache first
    const now = Date.now();
    if (this.volunteersCache && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
      console.log('✅ Returning cached volunteers data');
      return this.volunteersCache;
    }

    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      // Get the volunteer list
      const listsResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists?$select=id,displayName,name,webUrl`
      );
      
      const volunteerList = listsResponse.value.find((list: any) => 
        list.displayName === 'True Care Clinic Volunteer List' || 
        list.displayName === 'Volunteer List' ||
        list.displayName === 'Volunteers' ||
        (list.displayName.toLowerCase().includes('volunteer') && 
         !list.displayName.toLowerCase().includes('availability') &&
         !list.displayName.toLowerCase().includes('q1') &&
         !list.displayName.toLowerCase().includes('q2') &&
         !list.displayName.toLowerCase().includes('q3') &&
         !list.displayName.toLowerCase().includes('q4')) ||
        list.displayName.toLowerCase().includes('contact') ||
        list.displayName.toLowerCase().includes('people')
      );
      
      if (!volunteerList) {
        console.log('❌ No volunteer list found');
        throw new Error('Volunteer list not found. Please ensure the "True Care Clinic Volunteer List" exists in SharePoint.');
      }
      
      // Fetch volunteers with only essential fields for better performance
      // Using a simpler query to avoid OData parsing issues
      const response = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${volunteerList.id}/items?$expand=fields&$top=5000`
      );

      if (response.value.length === 0) {
        console.log('⚠️ No volunteer items found in SharePoint list');
        this.volunteersCache = [];
        this.cacheTimestamp = now;
        return [];
      }

      // Get comment counts in parallel for better performance
      const commentCountsPromise = this.getAllCommentCounts();
      
      // Process volunteers
      const volunteers = response.value.map((item: any) => {
        const fields = item.fields || {};
        
        // Find availability fields dynamically
        const foundAvailabilityFields = this.findAvailabilityFields(fields);
        
        // Find area fields dynamically
        const foundAreaFields = this.findAreaFields(fields);
        
        // Extract primary and secondary areas
        let primaryArea = foundAreaFields.primaryArea || fields['Primary Area'] || fields.PrimaryArea || fields.PrimaryRole || fields['Primary Role'] || '';
        let secondaryArea = foundAreaFields.secondaryArea || fields['Secondary Area'] || fields.SecondaryArea || fields.SecondaryRole || fields['Secondary Role'] || '';
        
        // If we don't have separate primary/secondary areas, try to extract ONLY primary from VolunteerRoleInterest
        // Avoid setting a default secondary (e.g., 'Greeters')
        if (!primaryArea && fields.VolunteerRoleInterest) {
          const roleInterests = Array.isArray(fields.VolunteerRoleInterest) 
            ? fields.VolunteerRoleInterest 
            : fields.VolunteerRoleInterest.split(',').map((s: string) => s.trim());
          if (roleInterests.length > 0) {
            primaryArea = roleInterests[0];
          }
        }
        
        // Map SharePoint fields to our Volunteer interface
        const volunteer: Volunteer = {
          id: item.id,
          name: fields.Title || fields.Name || fields.FullName || 'Unknown',
          email: fields.Email || fields.EMail || fields.EmailAddress || '',
          phone: fields.Phone || fields.Telephone || fields.PhoneNumber || '',
          volunteerStatus: fields.ContactStatus || fields.Status || VolunteerStatus.CONTACT_COMPLETE,
          availability: fields.Availability || fields.Available || fields.Schedule || '',
          assignedManager: fields.AssignedManager || fields.Manager || fields.Supervisor || '',
          whatsapp: fields.WhatsApp || fields.Phone || '',
          notes: fields.Notes || fields.Description || fields.Comments || '',
          lastClinicServed: fields.LastClinicServed || fields.LastClinic || fields.ClinicDate || '',
          createdAt: item.createdDateTime || fields.Created || '',
          businessUnit: fields.BusinessUnit || fields.Unit || fields.Department || '',
          assignedRole: fields.AssignedRole || fields.Role || fields.Position || '',
          buManager: fields.BUManager || fields.BusinessUnitManager || fields.Manager || '',
          primaryArea: primaryArea,
          secondaryArea: secondaryArea,
          januaryAvailability: foundAvailabilityFields.januaryAvailability || fields['January Availability'] || fields.JanuaryAvailability || fields.January || '',
          februaryAvailability: foundAvailabilityFields.februaryAvailability || fields['February Availability'] || fields.FebruaryAvailability || fields.February || '',
          marchAvailability: foundAvailabilityFields.marchAvailability || fields['March Availability'] || fields.MarchAvailability || fields.March || '',
          aprilAvailability: foundAvailabilityFields.aprilAvailability || fields['April Availability'] || fields.AprilAvailability || fields.April || '',
          mayAvailability: foundAvailabilityFields.mayAvailability || fields['May Availability'] || fields.MayAvailability || fields.May || '',
          juneAvailability: foundAvailabilityFields.juneAvailability || fields['June Availability'] || fields.JuneAvailability || fields.June || '',
          julyAvailability: foundAvailabilityFields.julyAvailability || fields['July Availability'] || fields.JulyAvailability || fields.July || '',
          augustAvailability: foundAvailabilityFields.augustAvailability || fields['August Availability'] || fields.AugustAvailability || fields.August || '',
          septemberAvailability: foundAvailabilityFields.septemberAvailability || fields['September Availability'] || fields.SeptemberAvailability || fields.September || '',
          octoberAvailability: foundAvailabilityFields.octoberAvailability || fields['October Availability'] || fields.OctoberAvailability || fields.October || '',
          novemberAvailability: foundAvailabilityFields.novemberAvailability || fields['November Availability'] || fields.NovemberAvailability || fields.November || '',
          decemberAvailability: foundAvailabilityFields.decemberAvailability || fields['December Availability'] || fields.DecemberAvailability || fields.December || '',
          campus: fields.Whatcampusdoyouattend_x003f_ || fields.Campus || fields.Location || '',
          preferredContact: fields.PreferredMethodOfContact || fields.PreferredContact || fields.ContactMethod || '',
          bestTimeToContact: fields.BestDate_x002f_TimetoContact || fields.BestTimeToContact || fields.ContactTime || '',
          volunteerRoleInterest: fields.VolunteerRoleInterest || fields.RoleInterest || '',
          additionalInformation: fields.AdditionalInformation || fields.Notes || '',
          volunteerTShirtSize: fields.VolunteerT_x002d_ShirtSize || fields.VolunteerTShirtSize || fields.TShirtSize || '',
          addedToWhatsapp: this.convertToBoolean(fields.Addedtowhatsapp_x003f_) || this.convertToBoolean(fields.AddedToWhatsapp) || this.convertToBoolean(fields.WhatsAppAdded) || false,
          orientationCompleted: this.convertToBoolean(fields.OrientationCompleted_x003f_) || this.convertToBoolean(fields.OrientationCompleted) || this.convertToBoolean(fields.Orientation) || false,
          backgroundCleared: this.convertToBoolean(fields.BackgroundCheckCleared_x003f_) || this.convertToBoolean(fields.BackgroundCleared) || this.convertToBoolean(fields.Background) || false,
          confidentiality: this.convertToBoolean(fields.CredentialVerification_x0028_IfM) || this.convertToBoolean(fields.Confidentiality) || this.convertToBoolean(fields.ConfidentialitySigned) || false,
          volunteerBirthdate: fields.VolunteerBirthdate || fields.Birthdate || '',
          mailingAddress: fields.MailingAddress || fields.Address || '',
          hipaaPrivacyTrainingCompleted: fields.HIPAAPrivacyTrainingCompleted_x0 || '',
          hipaaSecurityTrainingCompleted: fields.HIPPASecurityTrainingCompleted_x || '',
          scheduledForUpcomingClinic: fields.ScheduledforupcomingClinic_x003f || '',
          doYouHaveVolunteerTShirt: fields.DoyouhaveavolunteerT_x002d_Shirt || '',
          orientationAssigned: fields.OrientationAssigned || '',
          consentFormSent: fields.ConsentFormSent_x003f_ || '',
          title: fields.Title || fields.Name || fields.FullName || 'Unknown',
          status: fields.ContactStatus || fields.Status || VolunteerStatus.CONTACT_COMPLETE,
          commentCount: 0, // Will be updated after comment counts are loaded
        };
        
        return volunteer;
      });
      
      // Wait for comment counts and update volunteers
      const commentCounts = await commentCountsPromise;
      volunteers.forEach((volunteer: Volunteer) => {
        volunteer.commentCount = commentCounts[volunteer.id] || 0;
      });
      
      // Cache the results
      this.volunteersCache = volunteers;
      this.cacheTimestamp = now;
      
      console.log(`✅ Loaded ${volunteers.length} volunteers successfully`);
      return volunteers;
      
    } catch (error) {
      console.error('❌ Error fetching volunteers from SharePoint:', error);
      
      // If the list doesn't exist, try to create it
      if (error instanceof Error && error.message && error.message.includes('404')) {
        console.log('📝 Volunteer list not found, creating it...');
        try {
          await this.createVolunteerList();
          console.log('✅ Volunteer list created successfully');
          return [];
        } catch (createError) {
          console.error('❌ Failed to create volunteer list:', createError);
          throw new Error('Failed to create volunteer list. Please check SharePoint permissions and try again.');
        }
      }
      
      throw error;
    }
  }

  // Clear cache (call this when data is updated)
  clearCache(): void {
    this.volunteersCache = null;
    this.commentCountsCache = null;
    this.cacheTimestamp = 0;
    console.log('🗑️ Cache cleared');
  }

  // Force refresh comment counts
  clearCommentCountsCache(): void {
    this.commentCountsCache = null;
    console.log('🗑️ Comment counts cache cleared');
  }

  // Debug function to test comment counts
  async debugCommentCounts(): Promise<void> {
    console.log('🔍 Debugging comment counts...');
    this.clearCommentCountsCache();
    const counts = await this.getAllCommentCounts();
    console.log('📊 Comment counts:', counts);
  }

  // Debug function specifically for Business Unit fields
  async debugBusinessUnitFields(): Promise<void> {
    console.log('🔍 Debugging Business Unit fields...');
    
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      // Get the volunteer list schema
      const schema = await this.getVolunteerListSchema();
      console.log('📋 Full SharePoint List Schema:', schema);
      
      // Look for business unit related fields
      const businessUnitFields = schema.filter((field: any) => 
        field.name.toLowerCase().includes('business') || 
        field.name.toLowerCase().includes('unit') ||
        field.name.toLowerCase().includes('department') ||
        field.name.toLowerCase().includes('bu') ||
        field.name.toLowerCase().includes('manager') ||
        field.name.toLowerCase().includes('role')
      );
      console.log('🏢 Business Unit related fields found:', businessUnitFields);
      
      // Get a sample volunteer to see actual field names and values
      const response = await this.makeGraphRequest(
        `/sites/${siteId}/lists/True%20Care%20Clinic%20Volunteer%20List/items?$top=3&$expand=fields`
      );
      
      if (response.value && response.value.length > 0) {
        console.log('📋 Sample volunteer fields:', Object.keys(response.value[0].fields));
        
        response.value.forEach((item: any, index: number) => {
          const fields = item.fields;
          console.log(`\n📋 Volunteer ${index + 1} (${fields.Title || fields.Name || 'Unknown'}):`);
          console.log('  - Business Unit:', fields.BusinessUnit || fields.Unit || fields.Department || 'Not found');
          console.log('  - BU Manager:', fields.BUManager || fields.BusinessUnitManager || fields.Manager || 'Not found');
          console.log('  - Assigned Role:', fields.AssignedRole || fields.Role || fields.Position || 'Not found');
          console.log('  - All fields:', Object.keys(fields));
        });
      }
      
      // Test the field mapping logic
      console.log('\n🔍 Testing field mapping logic:');
      const testFields = ['BusinessUnit', 'Unit', 'Department'];
      const foundColumn = this.findSharePointColumnName(testFields, schema);
      console.log('  - Business Unit column found:', foundColumn);
      
      const testManagerFields = ['BUManager', 'BusinessUnitManager', 'Manager'];
      const foundManagerColumn = this.findSharePointColumnName(testManagerFields, schema);
      console.log('  - BU Manager column found:', foundManagerColumn);
      
      const testRoleFields = ['AssignedRole', 'Role', 'Position'];
      const foundRoleColumn = this.findSharePointColumnName(testRoleFields, schema);
      console.log('  - Assigned Role column found:', foundRoleColumn);
      
    } catch (error) {
      console.error('❌ Error debugging Business Unit fields:', error);
    }
  }

  // Optimized comment counts fetching with caching
  async getAllCommentCounts(): Promise<Record<string, number>> {
    // Check cache first
    if (this.commentCountsCache) {
      console.log('📊 Using cached comment counts');
      return this.commentCountsCache;
    }

    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      console.log('🔍 Getting comment counts from SharePoint...');
      
      // Get the Volunteer Comments list
      const listResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/Volunteer%20Comments`
      );
      
      console.log('📋 Volunteer Comments list found:', listResponse.id);
      
      // Get the list schema
      const schemaResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/columns`
      );
      
      console.log('📋 Schema columns:', schemaResponse.value.map((col: any) => ({ name: col.name, displayName: col.displayName })));
      
      // Find the volunteer ID field
      const volunteerField = schemaResponse.value.find((col: any) => 
        col.name === 'Id0' || col.displayName === 'Id'
      );
      
      if (!volunteerField) {
        console.log('❌ No volunteer ID field found, returning empty counts');
        this.commentCountsCache = {};
        return {};
      }
      
      console.log('🔍 Found volunteer ID field:', volunteerField.name);
      
      // Get all comments with selective fields - use same approach as getVolunteerCommentsFromList
      const allCommentsResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/items?$top=5000&$orderby=fields/Created desc&$expand=fields`,
        {},
        true // Allow non-indexed queries
      );
      
      console.log('📋 All comments response:', allCommentsResponse);
      console.log('📋 Number of comments found:', allCommentsResponse.value?.length || 0);
      
      if (!allCommentsResponse.value || allCommentsResponse.value.length === 0) {
        console.log('📝 No comments found, returning empty counts');
        this.commentCountsCache = {};
        return {};
      }
      
      // Log first few comments to see their structure
      console.log('🔍 First 3 comment items:');
      allCommentsResponse.value.slice(0, 3).forEach((item: any, index: number) => {
        console.log(`   Comment ${index + 1}:`, item);
        console.log(`   Comment ${index + 1} fields:`, item.fields);
        console.log(`   Comment ${index + 1} Id0:`, item.fields?.Id0);
        console.log(`   Comment ${index + 1} LinkTitle:`, item.fields?.LinkTitle);
      });
      
      // Count comments per volunteer
      const commentCounts: Record<string, number> = {};
      
      allCommentsResponse.value.forEach((item: any) => {
        const volunteerId = item.fields?.[volunteerField.name];
        console.log(`🔍 Item ${item.id} has volunteer ID: "${volunteerId}" (type: ${typeof volunteerId})`);
        
        if (volunteerId) {
          commentCounts[volunteerId] = (commentCounts[volunteerId] || 0) + 1;
        }
      });
      
      console.log('📊 Final comment counts:', commentCounts);
      this.commentCountsCache = commentCounts;
      console.log(`📊 Comment counts loaded for ${Object.keys(commentCounts).length} volunteers`);
      return commentCounts;
      
    } catch (error) {
      console.error('❌ Error fetching comment counts:', error);
      this.commentCountsCache = {};
      return {};
    }
  }

  // Helper function to convert SharePoint boolean values to JavaScript boolean
  private convertToBoolean(value: any): boolean {
    if (value === true || value === 'true' || value === 'True' || value === 'YES' || value === 'Yes' || value === 'yes' || value === 1) {
      return true;
    }
    return false;
  }

  // Helper function to find the correct SharePoint column name
  private findSharePointColumnName(possibleNames: string[], schema: any[]): string | null {
    // Check both internal name and displayName
    for (const name of possibleNames) {
      const found = schema.find((col: any) => 
        col.name === name || 
        col.displayName === name ||
        col.name?.toLowerCase() === name.toLowerCase() ||
        col.displayName?.toLowerCase() === name.toLowerCase()
      );
      if (found) {
        // Return the internal name (required for API calls)
        return found.name;
      }
    }
    return null;
  }

  async updateVolunteer(volunteerId: string, updates: Partial<Volunteer>): Promise<void> {
    console.log('🔍 updateVolunteer called with:', { volunteerId, updates });
    
    try {
      const siteId = await this.getSiteId();
      console.log('🔍 Got site ID:', siteId);
      
      // Get the schema to find correct column names
      const schema = await this.getVolunteerListSchema();
      console.log('🔍 Got volunteer list schema, columns:', schema.length);
      
      // Map our field names to SharePoint column names (using same logic as reading)
      const sharePointUpdates: any = {};
      
      if (updates.name !== undefined) {
        const nameColumn = this.findSharePointColumnName(['Title', 'Name', 'FullName'], schema);
        if (nameColumn) sharePointUpdates[nameColumn] = updates.name;
      }

      if (updates.email !== undefined) {
        const emailColumn = this.findSharePointColumnName(['Email', 'EmailAddress'], schema);
        if (emailColumn) sharePointUpdates[emailColumn] = updates.email;
      }

      if (updates.phone !== undefined) {
        const phoneColumn = this.findSharePointColumnName(['Phone', 'PhoneNumber', 'ContactNumber'], schema);
        if (phoneColumn) sharePointUpdates[phoneColumn] = updates.phone;
      }

      if (updates.volunteerStatus !== undefined) {
        const statusColumn = this.findSharePointColumnName(['ContactStatus', 'Status'], schema);
        if (statusColumn) sharePointUpdates[statusColumn] = updates.volunteerStatus;
      }

      if (updates.availability !== undefined) {
        const availabilityColumn = this.findSharePointColumnName(['Availability', 'Available', 'Schedule'], schema);
        if (availabilityColumn) sharePointUpdates[availabilityColumn] = updates.availability;
      }
      
      if (updates.lastClinicServed !== undefined) {
        const clinicColumn = this.findSharePointColumnName(['LastClinicServed', 'LastClinic', 'ClinicDate'], schema);
        if (clinicColumn) sharePointUpdates[clinicColumn] = updates.lastClinicServed;
      }
      
      if (updates.businessUnit !== undefined) {
        const unitColumn = this.findSharePointColumnName(['BusinessUnit', 'Unit', 'Department'], schema);
        if (unitColumn) sharePointUpdates[unitColumn] = updates.businessUnit;
      }
      
      if (updates.assignedRole !== undefined) {
        const roleColumn = this.findSharePointColumnName(['AssignedRole', 'Role', 'Position'], schema);
        if (roleColumn) sharePointUpdates[roleColumn] = updates.assignedRole;
      }
      
      if (updates.buManager !== undefined) {
        const buManagerColumn = this.findSharePointColumnName(['BUManager', 'BusinessUnitManager', 'Manager'], schema);
        if (buManagerColumn) sharePointUpdates[buManagerColumn] = updates.buManager;
      }
      
      if (updates.notes !== undefined) {
        const notesColumn = this.findSharePointColumnName(['Notes', 'Description', 'Comments'], schema);
        if (notesColumn) sharePointUpdates[notesColumn] = updates.notes;
      }
      
      if (updates.assignedManager !== undefined) {
        const managerColumn = this.findSharePointColumnName(['AssignedManager', 'Manager', 'Supervisor'], schema);
        if (managerColumn) sharePointUpdates[managerColumn] = updates.assignedManager;
      }
      
      if (updates.whatsapp !== undefined) {
        const whatsappColumn = this.findSharePointColumnName(['WhatsApp', 'Phone'], schema);
        if (whatsappColumn) sharePointUpdates[whatsappColumn] = updates.whatsapp;
      }
      
      if (updates.primaryArea !== undefined) {
        const primaryAreaColumn = this.findSharePointColumnName([
          'AssignedArea_x0028_Volunteer_x00',
          'Primary Area',
          'PrimaryArea',
          'PrimaryRole',
          'Primary Role'
        ], schema);
        if (primaryAreaColumn) {
          sharePointUpdates[primaryAreaColumn] = updates.primaryArea;
          console.log('🔍 Primary Area column found:', primaryAreaColumn);
        }
      }
      
      if (updates.secondaryArea !== undefined) {
        const secondaryAreaColumn = this.findSharePointColumnName([
          'Secondary Area',
          'SecondaryArea',
          'SecondaryRole',
          'Secondary Role'
        ], schema);
        if (secondaryAreaColumn) {
          sharePointUpdates[secondaryAreaColumn] = updates.secondaryArea;
          console.log('🔍 Secondary Area column found:', secondaryAreaColumn, 'Value:', updates.secondaryArea);
        } else {
          console.error('❌ Secondary Area column NOT FOUND in schema! Available columns:', schema.map((col: any) => ({ name: col.name, displayName: col.displayName })));
          // Don't fail silently - throw an error so user knows
          throw new Error('Secondary Area column not found in SharePoint list. Please check the list schema.');
        }
      }
      
      // Compliance fields
      if (updates.orientationCompleted !== undefined) {
        const orientationColumn = this.findSharePointColumnName(['OrientationCompleted_x003f_', 'OrientationCompleted', 'Orientation'], schema);
        console.log('🔍 Orientation column found:', orientationColumn);
        if (orientationColumn) sharePointUpdates[orientationColumn] = updates.orientationCompleted;
      }
      
      if (updates.backgroundCleared !== undefined) {
        const backgroundColumn = this.findSharePointColumnName(['BackgroundCheckCleared_x003f_', 'BackgroundCleared', 'Background'], schema);
        console.log('🔍 Background column found:', backgroundColumn);
        if (backgroundColumn) sharePointUpdates[backgroundColumn] = updates.backgroundCleared;
      }
      
      if (updates.confidentiality !== undefined) {
        const confidentialityColumn = this.findSharePointColumnName(['CredentialVerification_x0028_IfM', 'Confidentiality', 'ConfidentialitySigned'], schema);
        console.log('🔍 Confidentiality column found:', confidentialityColumn);
        if (confidentialityColumn) sharePointUpdates[confidentialityColumn] = updates.confidentiality;
      }
      
      if (updates.addedToWhatsapp !== undefined) {
        const whatsappColumn = this.findSharePointColumnName(['Addedtowhatsapp_x003f_', 'AddedToWhatsapp', 'WhatsAppAdded'], schema);
        console.log('🔍 WhatsApp column found:', whatsappColumn);
        if (whatsappColumn) sharePointUpdates[whatsappColumn] = updates.addedToWhatsapp;
      }
      
      console.log('Updating volunteer with SharePoint fields:', sharePointUpdates);
      console.log('🔍 Field names being sent:', Object.keys(sharePointUpdates));
      console.log('🔍 Compliance updates:', {
        orientationCompleted: updates.orientationCompleted,
        backgroundCleared: updates.backgroundCleared,
        confidentiality: updates.confidentiality,
        addedToWhatsapp: updates.addedToWhatsapp
      });
      
      console.log('🔍 Making Graph API PATCH request to update volunteer');
      console.log('🔍 Request URL:', `/sites/${siteId}/lists/True Care Clinic Volunteer List/items/${volunteerId}/fields`);
      console.log('🔍 Request body:', JSON.stringify(sharePointUpdates, null, 2));
      
      const response = await this.makeGraphRequest(
        `/sites/${siteId}/lists/True Care Clinic Volunteer List/items/${volunteerId}/fields`,
        {
          method: 'PATCH',
          body: JSON.stringify(sharePointUpdates),
        }
      );
      
      console.log('🔍 Graph API response:', response);
      
      // Clear cache since data was updated
      this.clearCache();
      
      console.log('✅ Successfully updated volunteer in SharePoint');

      // Notify other open pages/tabs to refresh their data
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('volunteer-updated', { detail: { volunteerId, updates } }));
        }
      } catch (e) {
        // ignore if CustomEvent not available
      }
    } catch (error) {
      console.error('Error updating volunteer:', error);
      throw error;
    }
  }

  // Clinic Schedule Operations
  async getClinicSchedule(): Promise<ClinicSchedule[]> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      const response = await this.makeGraphRequest(
        `/sites/${siteId}/lists/ClinicSchedule/items?$expand=fields`
      );

      return response.value.map((item: any) => ({
        id: item.id,
        title: item.fields.Title,
        date: item.fields.Date,
        startTime: item.fields.StartTime,
        endTime: item.fields.EndTime,
        volunteers: item.fields.Volunteers ? JSON.parse(item.fields.Volunteers) : [],
        notes: item.fields.Notes,
        status: item.fields.Status,
      }));
    } catch (error) {
      console.error('Error fetching clinic schedule:', error);
      throw error;
    }
  }

  async updateClinicSchedule(scheduleId: string, updates: Partial<ClinicSchedule>): Promise<void> {
    try {
      const siteId = await this.getSiteId();
      await this.makeGraphRequest(
        `/sites/${siteId}/lists/ClinicSchedule/items/${scheduleId}/fields`,
        {
          method: 'PATCH',
          body: JSON.stringify(updates),
        }
      );
    } catch (error) {
      console.error('Error updating clinic schedule:', error);
      throw error;
    }
  }

  // Calendar Events Operations
  async getCalendarEvents(): Promise<any[]> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      // Try to get events from CalendarEvents list
      try {
        const response = await this.makeGraphRequest(
          `/sites/${siteId}/lists/CalendarEvents/items?$expand=fields&$orderby=fields/EventDate asc`
        );
        
        return response.value.map((item: any) => ({
          id: item.id,
          title: item.fields.Title || item.fields.EventTitle || '',
          date: item.fields.EventDate ? item.fields.EventDate.split('T')[0] : item.fields.Date || '',
          startTime: item.fields.StartTime || item.fields.Start || item.fields.TimeStart || '',
          endTime: item.fields.EndTime || item.fields.End || item.fields.TimeEnd || '',
          department: item.fields.Department || item.fields.BusinessUnit || '',
          location: item.fields.Location || '',
          description: item.fields.Description || item.fields.Notes || '',
          attendeesCsv: item.fields.Attendees || '',
        }));
      } catch (listError: any) {
        // If list doesn't exist, create it
        if (listError && (listError.message?.includes('404') || listError.message?.includes('not found'))) {
          console.log('📅 CalendarEvents list not found, creating it...');
          await this.createCalendarEventsList();
          return [];
        }
        throw listError;
      }
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      return [];
    }
  }

  async createCalendarEvent(eventData: {
    title: string;
    date: string;
    startTime?: string;
    endTime?: string;
    department?: string;
    location?: string;
    description?: string;
    attendeesCsv?: string;
  }): Promise<string> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      // Ensure list exists
      try {
        await this.makeGraphRequest(`/sites/${siteId}/lists/CalendarEvents`);
      } catch {
        await this.createCalendarEventsList();
      }
      
      const eventDate = new Date(eventData.date);
      if (eventData.startTime) {
        const [hours, minutes] = eventData.startTime.split(':');
        eventDate.setHours(parseInt(hours), parseInt(minutes));
      }
      
      const response = await this.makeGraphRequest(
        `/sites/${siteId}/lists/CalendarEvents/items`,
        {
          method: 'POST',
          body: JSON.stringify({
            fields: {
              Title: eventData.title,
              EventDate: eventDate.toISOString(),
              Date: eventData.date,
              StartTime: eventData.startTime || '',
              EndTime: eventData.endTime || '',
              Department: eventData.department || '',
              Location: eventData.location || '',
              Description: eventData.description || '',
              Attendees: eventData.attendeesCsv || '',
            }
          })
        }
      );
      
      console.log('✅ Calendar event created:', response);
      this.clearCache();
      return response.id;
    } catch (error) {
      console.error('Error creating calendar event:', error);
      throw error;
    }
  }

  async updateCalendarEvent(eventId: string, eventData: Partial<{
    title: string;
    date: string;
    startTime?: string;
    endTime?: string;
    department?: string;
    location?: string;
    description?: string;
    attendeesCsv?: string;
  }>): Promise<void> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      const updates: any = {};
      if (eventData.title !== undefined) updates.Title = eventData.title;
      if (eventData.date !== undefined) {
        updates.Date = eventData.date;
        const eventDate = new Date(eventData.date);
        if (eventData.startTime) {
          const [hours, minutes] = eventData.startTime.split(':');
          eventDate.setHours(parseInt(hours), parseInt(minutes));
        }
        updates.EventDate = eventDate.toISOString();
      }
      if (eventData.startTime !== undefined) updates.StartTime = eventData.startTime;
      if (eventData.endTime !== undefined) updates.EndTime = eventData.endTime;
      if (eventData.department !== undefined) updates.Department = eventData.department;
      if (eventData.location !== undefined) updates.Location = eventData.location;
      if (eventData.description !== undefined) updates.Description = eventData.description;
      if (eventData.attendeesCsv !== undefined) updates.Attendees = eventData.attendeesCsv;
      
      await this.makeGraphRequest(
        `/sites/${siteId}/lists/CalendarEvents/items/${eventId}/fields`,
        {
          method: 'PATCH',
          body: JSON.stringify(updates),
        }
      );
      
      console.log('✅ Calendar event updated');
      this.clearCache();
    } catch (error) {
      console.error('Error updating calendar event:', error);
      throw error;
    }
  }

  async deleteCalendarEvent(eventId: string): Promise<void> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      await this.makeGraphRequest(
        `/sites/${siteId}/lists/CalendarEvents/items/${eventId}`,
        {
          method: 'DELETE',
        }
      );
      
      console.log('✅ Calendar event deleted');
      this.clearCache();
    } catch (error) {
      console.error('Error deleting calendar event:', error);
      throw error;
    }
  }

  private async createCalendarEventsList(): Promise<void> {
    try {
      const siteId = await this.getSiteId();
      await this.makeGraphRequest(
        `/sites/${siteId}/lists`,
        {
          method: 'POST',
          body: JSON.stringify({
            displayName: 'CalendarEvents',
            columns: [
              {
                name: 'Title',
                column: {
                  name: 'Title',
                  text: { maxLength: 255 }
                }
              },
              {
                name: 'EventDate',
                column: {
                  name: 'EventDate',
                  dateTime: { displayFormat: 0 }
                }
              },
              {
                name: 'Date',
                column: {
                  name: 'Date',
                  text: { maxLength: 10 }
                }
              },
              {
                name: 'StartTime',
                column: {
                  name: 'StartTime',
                  text: { maxLength: 10 }
                }
              },
              {
                name: 'EndTime',
                column: {
                  name: 'EndTime',
                  text: { maxLength: 10 }
                }
              },
              {
                name: 'Department',
                column: {
                  name: 'Department',
                  text: { maxLength: 100 }
                }
              },
              {
                name: 'Location',
                column: {
                  name: 'Location',
                  text: { maxLength: 255 }
                }
              },
              {
                name: 'Description',
                column: {
                  name: 'Description',
                  text: { allowMultipleLines: true, maxLength: 2000 }
                }
              },
              {
                name: 'Attendees',
                column: {
                  name: 'Attendees',
                  text: { maxLength: 500 }
                }
              }
            ]
          })
        }
      );
      console.log('✅ Created CalendarEvents list');
    } catch (error: any) {
      console.log('⚠️ CalendarEvents list already exists or creation failed:', error?.message || error);
    }
  }

  // Activity Log Operations
  async getActivityLog(): Promise<ActivityLog[]> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      const response = await this.makeGraphRequest(
        `/sites/${siteId}/lists/ActivityLog/items?$expand=fields&$orderby=fields/Timestamp desc`,
        {},
        true // Allow non-indexed queries for the Timestamp field
      );

      return response.value.map((item: any) => ({
        id: item.id,
        sender: item.fields.Sender,
        mentionedUser: item.fields.MentionedUser,
        message: item.fields.Message,
        timestamp: item.fields.Timestamp,
        type: item.fields.Type,
      }));
    } catch (error) {
      console.error('Error fetching activity log:', error);
      throw error;
    }
  }

  async addActivityLog(activity: Omit<ActivityLog, 'id' | 'timestamp'>): Promise<void> {
    try {
      const siteId = await this.getSiteId();
      await this.makeGraphRequest(
        `/sites/${siteId}/lists/ActivityLog/items`,
        {
          method: 'POST',
          body: JSON.stringify({
            fields: {
              ...activity,
              Timestamp: new Date().toISOString(),
            },
          }),
        }
      );
    } catch (error) {
      console.error('Error adding activity log:', error);
      throw error;
    }
  }

  // Create necessary lists if they don't exist
  async createListsIfNeeded(): Promise<void> {
    try {
      const siteId = await this.getSiteId();
      
      // Check if user has permissions to create lists
      let hasCreatePermission = true;
      try {
        // Try a simple read operation first to check basic permissions
        await this.makeGraphRequest(`/sites/${siteId}/lists`, { method: 'GET' });
      } catch (permissionError) {
        console.log('⚠️ User may not have sufficient permissions:', permissionError);
        hasCreatePermission = false;
      }
      
      if (!hasCreatePermission) {
        console.log('ℹ️ Skipping list creation - user lacks sufficient permissions');
        return;
      }
      
      // Create Volunteer List
      try {
        await this.makeGraphRequest(
          `/sites/${siteId}/lists`,
          {
            method: 'POST',
            body: JSON.stringify({
              displayName: 'True Care Clinic Volunteer List',
              columns: [
                {
                  name: 'Title',
                  column: {
                    name: 'Title',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: true,
                    text: {
                      allowMultipleLines: false,
                      appendChangesToExistingText: false,
                      linesForEditing: 0,
                      maxLength: 255,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'Email',
                  column: {
                    name: 'Email',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: true,
                    text: {
                      allowMultipleLines: false,
                      appendChangesToExistingText: false,
                      linesForEditing: 0,
                      maxLength: 255,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'Phone',
                  column: {
                    name: 'Phone',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: false,
                    text: {
                      allowMultipleLines: false,
                      appendChangesToExistingText: false,
                      linesForEditing: 0,
                      maxLength: 255,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'Availability',
                  column: {
                    name: 'Availability',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: false,
                    text: {
                      allowMultipleLines: true,
                      appendChangesToExistingText: false,
                      linesForEditing: 3,
                      maxLength: 1000,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'AssignedManager',
                  column: {
                    name: 'AssignedManager',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: false,
                    text: {
                      allowMultipleLines: false,
                      appendChangesToExistingText: false,
                      linesForEditing: 0,
                      maxLength: 255,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'Status',
                  column: {
                    name: 'Status',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: false,
                    choice: {
                      allowTextEntry: false,
                      choices: ['Active', 'Inactive', 'Pending']
                    }
                  }
                },
                {
                  name: 'WhatsApp',
                  column: {
                    name: 'WhatsApp',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: false,
                    text: {
                      allowMultipleLines: false,
                      appendChangesToExistingText: false,
                      linesForEditing: 0,
                      maxLength: 255,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'Notes',
                  column: {
                    name: 'Notes',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: false,
                    text: {
                      allowMultipleLines: true,
                      appendChangesToExistingText: false,
                      linesForEditing: 5,
                      maxLength: 2000,
                      textType: 'plain'
                    }
                  }
                }
              ]
            })
          }
        );
        console.log('Created Volunteer List');
      } catch (error) {
        console.log('Volunteer List already exists or creation failed:', error);
      }

      // Create Clinic Schedule List
      try {
        await this.makeGraphRequest(
          `/sites/${siteId}/lists`,
          {
            method: 'POST',
            body: JSON.stringify({
              displayName: 'ClinicSchedule',
              columns: [
                {
                  name: 'Title',
                  column: {
                    name: 'Title',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: true,
                    text: {
                      allowMultipleLines: false,
                      appendChangesToExistingText: false,
                      linesForEditing: 0,
                      maxLength: 255,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'Date',
                  column: {
                    name: 'Date',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: true,
                    dateTime: {
                      displayFormat: 0
                    }
                  }
                },
                {
                  name: 'StartTime',
                  column: {
                    name: 'StartTime',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: true,
                    text: {
                      allowMultipleLines: false,
                      appendChangesToExistingText: false,
                      linesForEditing: 0,
                      maxLength: 10,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'EndTime',
                  column: {
                    name: 'EndTime',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: true,
                    text: {
                      allowMultipleLines: false,
                      appendChangesToExistingText: false,
                      linesForEditing: 0,
                      maxLength: 10,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'Volunteers',
                  column: {
                    name: 'Volunteers',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: false,
                    text: {
                      allowMultipleLines: true,
                      appendChangesToExistingText: false,
                      linesForEditing: 3,
                      maxLength: 1000,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'Notes',
                  column: {
                    name: 'Notes',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: false,
                    text: {
                      allowMultipleLines: true,
                      appendChangesToExistingText: false,
                      linesForEditing: 5,
                      maxLength: 2000,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'Status',
                  column: {
                    name: 'Status',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: false,
                    choice: {
                      allowTextEntry: false,
                      choices: ['Scheduled', 'Confirmed', 'Cancelled', 'Completed']
                    }
                  }
                }
              ]
            })
          }
        );
        console.log('Created Clinic Schedule List');
      } catch (error) {
        console.log('Clinic Schedule List already exists or creation failed:', error);
      }

      // Create Activity Log List
      try {
        await this.makeGraphRequest(
          `/sites/${siteId}/lists`,
          {
            method: 'POST',
            body: JSON.stringify({
              displayName: 'ActivityLog',
              columns: [
                {
                  name: 'Sender',
                  column: {
                    name: 'Sender',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: true,
                    text: {
                      allowMultipleLines: false,
                      appendChangesToExistingText: false,
                      linesForEditing: 0,
                      maxLength: 255,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'MentionedUser',
                  column: {
                    name: 'MentionedUser',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: false,
                    text: {
                      allowMultipleLines: false,
                      appendChangesToExistingText: false,
                      linesForEditing: 0,
                      maxLength: 255,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'Message',
                  column: {
                    name: 'Message',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: true,
                    text: {
                      allowMultipleLines: true,
                      appendChangesToExistingText: false,
                      linesForEditing: 5,
                      maxLength: 2000,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'Timestamp',
                  column: {
                    name: 'Timestamp',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: true,
                    dateTime: {
                      displayFormat: 0
                    }
                  }
                },
                {
                  name: 'Type',
                  column: {
                    name: 'Type',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: false,
                    choice: {
                      allowTextEntry: false,
                      choices: ['mention', 'comment', 'reminder']
                    }
                  }
                }
              ]
            })
          }
        );
        console.log('Created Activity Log List');
      } catch (error) {
        console.log('Activity Log List already exists or creation failed:', error);
      }

      // Create PrayerBox List
      try {
        await this.makeGraphRequest(
          `/sites/${siteId}/lists`,
          {
            method: 'POST',
            body: JSON.stringify({
              displayName: 'PrayerBox',
              columns: [
                {
                  name: 'Title',
                  column: {
                    name: 'Title',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: true,
                    text: {
                      allowMultipleLines: false,
                      appendChangesToExistingText: false,
                      linesForEditing: 0,
                      maxLength: 255,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'PrayerText',
                  column: {
                    name: 'PrayerText',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: true,
                    text: {
                      allowMultipleLines: true,
                      appendChangesToExistingText: false,
                      linesForEditing: 5,
                      maxLength: 2000,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'AssignedTo',
                  column: {
                    name: 'AssignedTo',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: false,
                    text: {
                      allowMultipleLines: false,
                      appendChangesToExistingText: false,
                      linesForEditing: 0,
                      maxLength: 255,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'IsAnswered',
                  column: {
                    name: 'IsAnswered',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: false,
                    boolean: {}
                  }
                }
              ]
            })
          }
        );
        console.log('Created PrayerBox List');
      } catch (error) {
        console.log('PrayerBox List already exists or creation failed:', error);
      }

      // Create Prayer Comments List
      try {
        await this.makeGraphRequest(
          `/sites/${siteId}/lists`,
          {
            method: 'POST',
            body: JSON.stringify({
              displayName: 'Prayer Comments',
              columns: [
                {
                  name: 'Title',
                  column: {
                    name: 'Title',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: true,
                    text: {
                      allowMultipleLines: false,
                      appendChangesToExistingText: false,
                      linesForEditing: 0,
                      maxLength: 255,
                      textType: 'plain'
                    }
                  }
                },
                {
                  name: 'PrayerId',
                  column: {
                    name: 'PrayerId',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: true,
                    number: {
                      decimalPlaces: 0,
                      maximum: 2147483647,
                      minimum: 0
                    }
                  }
                },
                {
                  name: 'CommentText',
                  column: {
                    name: 'CommentText',
                    enforceUniqueValues: false,
                    indexed: false,
                    nameAsDisplayName: true,
                    required: true,
                    text: {
                      allowMultipleLines: true,
                      appendChangesToExistingText: false,
                      linesForEditing: 5,
                      maxLength: 2000,
                      textType: 'plain'
                    }
                  }
                }
              ]
            })
          }
        );
        console.log('Created Prayer Comments List');
      } catch (error) {
        console.log('Prayer Comments List already exists or creation failed:', error);
      }

    } catch (error) {
      console.error('Error creating lists:', error);
      throw error;
    }
  }

  // Create the volunteer list specifically
  private async createVolunteerList(): Promise<void> {
    try {
      const siteId = await this.getSiteId();
      await this.makeGraphRequest(
        `/sites/${siteId}/lists`,
        {
          method: 'POST',
          body: JSON.stringify({
            displayName: 'True Care Clinic Volunteer List',
            columns: [
              {
                name: 'Title',
                column: {
                  name: 'Title',
                  enforceUniqueValues: false,
                  indexed: false,
                  nameAsDisplayName: true,
                  required: true,
                  text: {
                    allowMultipleLines: false,
                    appendChangesToExistingText: false,
                    linesForEditing: 0,
                    maxLength: 255,
                    textType: 'plain'
                  }
                }
              },
              {
                name: 'Email',
                column: {
                  name: 'Email',
                  enforceUniqueValues: false,
                  indexed: false,
                  nameAsDisplayName: true,
                  required: true,
                  text: {
                    allowMultipleLines: false,
                    appendChangesToExistingText: false,
                    linesForEditing: 0,
                    maxLength: 255,
                    textType: 'plain'
                  }
                }
              },
              {
                name: 'Phone',
                column: {
                  name: 'Phone',
                  enforceUniqueValues: false,
                  indexed: false,
                  nameAsDisplayName: true,
                  required: false,
                  text: {
                    allowMultipleLines: false,
                    appendChangesToExistingText: false,
                    linesForEditing: 0,
                    maxLength: 255,
                    textType: 'plain'
                  }
                }
              },
              {
                name: 'Availability',
                column: {
                  name: 'Availability',
                  enforceUniqueValues: false,
                  indexed: false,
                  nameAsDisplayName: true,
                  required: false,
                  text: {
                    allowMultipleLines: true,
                    appendChangesToExistingText: false,
                    linesForEditing: 3,
                    maxLength: 1000,
                    textType: 'plain'
                  }
                }
              },
              {
                name: 'AssignedManager',
                column: {
                  name: 'AssignedManager',
                  enforceUniqueValues: false,
                  indexed: false,
                  nameAsDisplayName: true,
                  required: false,
                  text: {
                    allowMultipleLines: false,
                    appendChangesToExistingText: false,
                    linesForEditing: 0,
                    maxLength: 255,
                    textType: 'plain'
                  }
                }
              },
              {
                name: 'Status',
                column: {
                  name: 'Status',
                  enforceUniqueValues: false,
                  indexed: false,
                  nameAsDisplayName: true,
                  required: false,
                  choice: {
                    allowTextEntry: false,
                    choices: ['Active', 'Inactive', 'Pending']
                  }
                }
              },
              {
                name: 'WhatsApp',
                column: {
                  name: 'WhatsApp',
                  enforceUniqueValues: false,
                  indexed: false,
                  nameAsDisplayName: true,
                  required: false,
                  text: {
                    allowMultipleLines: false,
                    appendChangesToExistingText: false,
                    linesForEditing: 0,
                    maxLength: 255,
                    textType: 'plain'
                  }
                }
              },
              {
                name: 'Notes',
                column: {
                  name: 'Notes',
                  enforceUniqueValues: false,
                  indexed: false,
                  nameAsDisplayName: true,
                  required: false,
                  text: {
                    allowMultipleLines: true,
                    appendChangesToExistingText: false,
                    linesForEditing: 5,
                    maxLength: 2000,
                    textType: 'plain'
                  }
                }
              }
            ]
          })
        }
      );
    } catch (error) {
      console.error('Error creating volunteer list:', error);
      throw error;
    }
  }

  // Mock data for development
  private getMockVolunteers(): Volunteer[] {
    return [
      {
        id: '1',
        name: 'John Smith',
        email: 'john.smith@example.com',
        phone: '+1 (555) 123-4567',
        volunteerStatus: VolunteerStatus.CONTACT_COMPLETE,
        availability: 'Weekdays 9AM-5PM',
        assignedManager: 'Sarah Johnson',
        whatsapp: '+1 (555) 123-4567',
        notes: 'Experienced volunteer with medical background',
        lastClinicServed: '2024-01-15',
        businessUnit: 'Clinical',
        assignedRole: 'Medical Director (Providers)',
        campus: 'Downtown Campus',
        preferredContact: 'Email',
        bestTimeToContact: 'Weekdays 9AM-5PM',
        volunteerRoleInterest: 'Medical Assistant',
        additionalInformation: 'Has experience with patient care',
        volunteerTShirtSize: 'L',
        addedToWhatsapp: 'Yes',
        orientationCompleted: 'Yes',
        backgroundCleared: 'Yes',
        confidentiality: 'Yes',
        volunteerBirthdate: '1985-03-15',
        mailingAddress: '123 Main St, City, State 12345',
        // Legacy fields for backward compatibility
        title: 'John Smith',
        status: VolunteerStatus.CONTACT_COMPLETE,
      },
      {
        id: '2',
        name: 'Maria Garcia',
        email: 'maria.garcia@example.com',
        phone: '+1 (555) 234-5678',
        volunteerStatus: VolunteerStatus.CONTACT_COMPLETE,
        availability: 'Weekends 10AM-6PM',
        assignedManager: 'Mike Davis',
        whatsapp: '+1 (555) 234-5678',
        notes: 'Bilingual volunteer, speaks Spanish',
        lastClinicServed: '2024-01-20',
        businessUnit: 'Operational',
        assignedRole: 'Language Services Manager',
        campus: 'North Campus',
        preferredContact: 'Phone',
        bestTimeToContact: 'Weekends 10AM-6PM',
        volunteerRoleInterest: 'Interpreter',
        additionalInformation: 'Fluent in Spanish and English',
        volunteerTShirtSize: 'M',
        addedToWhatsapp: 'No',
        orientationCompleted: 'No',
        backgroundCleared: 'No',
        confidentiality: 'No',
        volunteerBirthdate: '1990-07-22',
        mailingAddress: '456 Oak Ave, City, State 12345',
        // Legacy fields for backward compatibility
        title: 'Maria Garcia',
        status: VolunteerStatus.CONTACT_COMPLETE,
      },
      {
        id: '3',
        name: 'David Wilson',
        email: 'david.wilson@example.com',
        phone: '+1 (555) 345-6789',
        volunteerStatus: VolunteerStatus.INACTIVE,
        availability: 'Evenings 6PM-10PM',
        assignedManager: 'Sarah Johnson',
        whatsapp: '+1 (555) 345-6789',
        notes: 'Former volunteer, may return in future',
        lastClinicServed: '2023-12-10',
        businessUnit: 'Administrative',
        assignedRole: 'Administrative Assistant',
        campus: 'South Campus',
        preferredContact: 'Email',
        bestTimeToContact: 'Evenings 6PM-10PM',
        volunteerRoleInterest: 'Administrative',
        additionalInformation: 'Good with paperwork and organization',
        volunteerTShirtSize: 'XL',
        addedToWhatsapp: 'Yes',
        orientationCompleted: 'Yes',
        backgroundCleared: 'Yes',
        confidentiality: 'Yes',
        volunteerBirthdate: '1982-11-08',
        mailingAddress: '789 Pine St, City, State 12345',
        // Legacy fields for backward compatibility
        title: 'David Wilson',
        status: VolunteerStatus.INACTIVE,
      },
    ];
  }

  private getMockClinicSchedule(): ClinicSchedule[] {
    return [
      {
        id: '1',
        title: 'General Health Check',
        date: '2024-01-15',
        startTime: '09:00',
        endTime: '17:00',
        volunteers: ['1', '2'],
        notes: 'Annual health screening event',
        status: ClinicStatus.SCHEDULED,
      },
      {
        id: '2',
        title: 'Vaccination Clinic',
        date: '2024-01-20',
        startTime: '10:00',
        endTime: '16:00',
        volunteers: ['2', '3'],
        notes: 'Flu and COVID-19 vaccinations',
        status: ClinicStatus.SCHEDULED,
      },
    ];
  }

  private getMockActivityLog(): ActivityLog[] {
    return [
      {
        id: '1',
        sender: 'Sarah Johnson',
        mentionedUser: 'John Smith',
        message: 'Assigned to General Health Check clinic on Jan 15',
        timestamp: '2024-01-10T10:30:00Z',
        type: ActivityType.MENTION,
      },
      {
        id: '2',
        sender: 'Sarah Johnson',
        mentionedUser: 'Michael Chen',
        message: 'Updated availability for weekend shifts',
        timestamp: '2024-01-09T14:15:00Z',
        type: ActivityType.COMMENT,
      },
    ];
  }



  // Get the Volunteer Comments list schema to see available fields
  async getVolunteerCommentsListSchema(): Promise<any> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      // Get the Volunteer Comments list
      const listResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/Volunteer%20Comments`
      );
      
      console.log('📋 Volunteer Comments list:', listResponse);
      
      // Get the list columns/schema
      const columnsResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/columns`
      );
      
      console.log('📋 Volunteer Comments list columns:', columnsResponse);
      
      return {
        list: listResponse,
        columns: columnsResponse
      };
    } catch (error) {
      console.error('❌ Error getting Volunteer Comments list schema:', error);
      throw error;
    }
  }

  // Get all lists in the site
  async getAllLists(): Promise<any> {
    try {
      const siteId = await this.getSiteId();
      const response = await this.makeGraphRequest(
        `/sites/${siteId}/lists?$select=id,displayName,name,webUrl`
      );
      console.log('All lists in site:', response.value);
      return response.value;
    } catch (error) {
      console.error('Error fetching all lists:', error);
      throw error;
    }
  }

  // Get all lists and their items for debugging
  async getAllListsWithItems(): Promise<any> {
    try {
      const siteId = await this.getSiteId();
      const listsResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists?$select=id,displayName,name,webUrl`
      );
      
      const result = [];
      for (const list of listsResponse.value) {
        try {
          const itemsResponse = await this.makeGraphRequest(
            `/sites/${siteId}/lists/${list.id}/items?$expand=fields&$top=5`
          );
          result.push({
            list: list,
            items: itemsResponse.value,
            itemCount: itemsResponse.value.length
          });
        } catch (error) {
          result.push({
            list: list,
            items: [],
            itemCount: 0,
            error: error instanceof Error ? error.message : String(error)
          });
        }
      }
      
      console.log('All lists with items:', result);
      return result;
    } catch (error) {
      console.error('Error fetching all lists with items:', error);
      throw error;
    }
  }

  // Get volunteers from a specific list
  async getVolunteersFromList(listName: string): Promise<Volunteer[]> {
    try {
      const siteId = await this.getSiteId();
      
      // Get all lists
      const listsResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists?$select=id,displayName,name,webUrl`
      );
      
      // Find the specific list
      const targetList = listsResponse.value.find((list: any) => 
        list.displayName === listName || 
        list.name === listName ||
        list.id === listName
      );
      
      if (!targetList) {
        console.error(`List "${listName}" not found. Available lists:`, 
          listsResponse.value.map((l: any) => l.displayName));
        throw new Error(`List "${listName}" not found. Please check the list name and try again.`);
      }
      
      console.log(`Using list: "${targetList.displayName}" (ID: ${targetList.id})`);
      
      // Get items from the specific list
      const response = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${targetList.id}/items?$expand=fields`
      );

      console.log('Raw data from list:', response.value);

      return response.value.map((item: any) => {
        const fields = item.fields || {};
        console.log('Processing item:', fields);
        
        return {
          id: item.id,
          title: fields.Title || fields.Name || fields.FullName || 'Unknown',
          email: fields.Email || fields.EMail || fields.EmailAddress || '',
          phone: fields.Phone || fields.Telephone || fields.PhoneNumber || '',
          availability: fields.Availability || fields.Available || fields.Schedule || '',
          assignedManager: fields.AssignedManager || fields.Manager || fields.Supervisor || '',
          status: fields.ContactStatus || fields.Status || VolunteerStatus.CONTACT_COMPLETE,
          whatsapp: fields.WhatsApp || fields.Phone || '',
          notes: fields.Notes || fields.Description || fields.Comments || '',
          lastClinicServed: fields.LastClinicServed || fields.LastClinic || fields.ClinicDate || '',
          businessUnit: fields.BusinessUnit || fields.Unit || fields.Department || '',
          assignedRole: fields.AssignedRole || fields.Role || fields.Position || '',
        };
      });
    } catch (error) {
      console.error('Error fetching volunteers from specific list:', error);
      throw error;
    }
  }

  // Debug function to check SharePoint field names
  async debugSharePointFields(): Promise<void> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      // Get the volunteer list schema
      const schema = await this.getVolunteerListSchema();
      console.log('🔍 SharePoint List Schema:', schema);
      
      // Look for campus-related fields
      const campusFields = schema.filter((field: any) => 
        field.name.toLowerCase().includes('campus') || 
        field.name.toLowerCase().includes('attend') ||
        field.name.toLowerCase().includes('location')
      );
      console.log('🏫 Campus-related fields found:', campusFields);
      
      // Get a sample volunteer to see actual field names
      const response = await this.makeGraphRequest(
        `/sites/${siteId}/lists/True%20Care%20Clinic%20Volunteer%20List/items?$top=1&$expand=fields`
      );
      
      if (response.value && response.value.length > 0) {
        const sampleFields = response.value[0].fields;
        console.log('📋 Sample volunteer fields:', Object.keys(sampleFields));
        console.log('🏫 Sample volunteer campus field:', sampleFields);
      }
    } catch (error) {
      console.error('❌ Error debugging SharePoint fields:', error);
    }
  }

  // Check if the Volunteer Comments list exists
  async checkVolunteerCommentsListExists(): Promise<boolean> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      // Try to get the Volunteer Comments list
      const listResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/Volunteer%20Comments`
      );
      
      console.log('✅ Volunteer Comments list exists:', listResponse);
      return true;
    } catch (error) {
      console.log('❌ Volunteer Comments list does not exist or is not accessible:', error);
      return false;
    }
  }

  // Get comments for a specific volunteer from the Volunteer Comments list
  async getVolunteerCommentsFromList(volunteerId: string): Promise<Comment[]> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      console.log('🔍 Starting getVolunteerCommentsFromList for volunteer ID:', volunteerId);
      console.log('🔍 Volunteer ID type:', typeof volunteerId);
      
      // Get the Volunteer Comments list
      const listResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/Volunteer%20Comments`
      );
      
      console.log('📋 Volunteer Comments list response:', listResponse);
      
      // Get the list schema
      console.log('🔍 Fetching list schema...');
      const schemaResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/columns`
      );
      
      console.log('📋 Volunteer Comments list schema:', schemaResponse);
      console.log('📋 Schema value:', schemaResponse.value);
      
      console.log('📋 All available columns:');
      schemaResponse.value.forEach((column: any, index: number) => {
        console.log(`   ${index + 1}. Name: "${column.name}", DisplayName: "${column.displayName}", Type: "${column.type}"`);
      });
      
      // Find the volunteer ID field - look for Id0 field specifically (this is where the volunteer ID is actually stored)
      const volunteerField = schemaResponse.value.find((col: any) => 
        col.name === 'Id0' || col.displayName === 'Id'
      );
      
      console.log('🔍 Looking for volunteer ID field...');
      console.log('🔍 Found volunteer ID field:', volunteerField);
      console.log('🔍 Volunteer field name being used for filtering:', volunteerField?.name);
      
      if (!volunteerField) {
        console.log('❌ No volunteer ID field (Id0) found in schema');
        return [];
      }
      
      console.log('🔍 Found volunteer ID field:', volunteerField);
      console.log('🔍 Volunteer field name being used for filtering:', volunteerField.name);
      
      // Instead of filtering by LinkTitle (which is not indexed), get all comments and filter client-side
      console.log('🔍 Getting ALL comments (will filter client-side)...');
      const allCommentsResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/items?$top=5000&$orderby=fields/Created desc&$expand=fields`,
        {},
        true // Allow non-indexed queries for the Created field
      );
      
      console.log('📋 All comments response:', allCommentsResponse);
      console.log('📋 Number of comments found:', allCommentsResponse.value?.length || 0);
      
      if (!allCommentsResponse.value || allCommentsResponse.value.length === 0) {
        console.log('📝 No comments found in the list');
        return [];
      }
      
      // Log all comment items to see their structure
      console.log('🔍 All comment items:');
      allCommentsResponse.value.forEach((item: any, index: number) => {
        console.log(`   Comment ${index + 1}:`, item);
        console.log(`   Comment ${index + 1} fields:`, item.fields);
        console.log(`   Comment ${index + 1} Id0:`, item.fields?.Id0);
        console.log(`   Comment ${index + 1} LinkTitle:`, item.fields?.LinkTitle);
      });
      
      // Filter comments client-side by volunteer ID
      const volunteerComments = allCommentsResponse.value.filter((item: any) => {
        const itemVolunteerId = item.fields?.[volunteerField.name];
        console.log(`🔍 Item ${item.id} has volunteer ID: "${itemVolunteerId}" (looking for: "${volunteerId}")`);
        console.log(`🔍 Item ${item.id} fields:`, item.fields);
        console.log(`🔍 Item ${item.id} full structure:`, JSON.stringify(item, null, 2));
        
        // Also check other potential volunteer ID fields
        const id0Value = item.fields?.Id0;
        const linkTitleValue = item.fields?.LinkTitle;
        console.log(`🔍 Item ${item.id} - Id0: "${id0Value}", LinkTitle: "${linkTitleValue}"`);
        
        return itemVolunteerId === volunteerId;
      });
      
      console.log(`📝 Found ${volunteerComments.length} comments for volunteer ${volunteerId}`);
      
      // Convert to Comment objects
      const comments = volunteerComments.map((item: any) => {
        console.log(`🔍 Processing comment item ${item.id}:`, item);
        
        const comment = {
          id: item.id,
          author: item.fields?.Author?.Title || item.createdBy?.user?.displayName || 'Unknown',
          authorEmail: item.fields?.Author?.EMail || item.createdBy?.user?.email || '',
          content: item.fields?.Comment || 'No content',
          timestamp: item.fields?.Created || item.createdDateTime || new Date().toISOString(),
          type: 'comment' as const
        };
        
        console.log(`🔍 Processed comment:`, comment);
        return comment;
      });
      
      console.log('📝 Processed comments:', comments);
      return comments;
      
    } catch (error) {
      console.error('❌ Error fetching volunteer comments from list:', error);
      throw error;
    }
  }

  // Get comments for a specific volunteer item
  async getVolunteerComments(volunteerId: string): Promise<Comment[]> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      console.log('🔍 Fetching comments for volunteer ID:', volunteerId);
      
      // Get the Volunteer Comments list
      const listResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/Volunteer%20Comments`
      );
      
      console.log('📋 Comments list response:', listResponse);
      
      // Get the list schema to find the volunteer ID field
      const schemaResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/columns`
      );
      
      // Find the volunteer ID field
      const volunteerField = schemaResponse.value.find((col: any) => 
        col.name === 'Id0' || col.displayName === 'Id'
      );
      
      if (!volunteerField) {
        console.log('❌ No volunteer ID field found in comments list');
        return [];
      }
      
      // Get comments for this specific volunteer - allow non-indexed queries
      const commentsResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/items?$filter=${volunteerField.name} eq '${volunteerId}'&$expand=fields&$orderby=Created desc`,
        {},
        true // Allow non-indexed queries
      );
      
      console.log('📝 Comments response:', commentsResponse);
      
      if (!commentsResponse.value || commentsResponse.value.length === 0) {
        console.log('📝 No comments found for volunteer:', volunteerId);
        return [];
      }
      
      // Process the comments
      const comments = commentsResponse.value.map((item: any) => ({
        id: item.id,
        author: item.fields?.Author?.Title || item.fields?.AuthorName || 'Unknown',
        authorEmail: item.fields?.Author?.EMail || item.fields?.AuthorEmail || '',
        content: item.fields?.Comment || item.fields?.Text || item.fields?.Content || 'No content',
        timestamp: item.fields?.Created || item.createdDateTime || new Date().toISOString(),
        type: 'comment' as const
      }));
      
      console.log('📝 Processed comments:', comments);
      return comments;
      
    } catch (error) {
      console.error('❌ Error fetching volunteer comments:', error);
      return [];
    }
  }

  // Add a comment to the Volunteer Comments list
  async addVolunteerCommentToList(volunteerId: string, volunteerName: string, comment: string): Promise<void> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      // Check if the list exists first
      const listExists = await this.checkVolunteerCommentsListExists();
      if (!listExists) {
        throw new Error('Volunteer Comments list not found. Please ensure the list exists in SharePoint.');
      }
      
      // Get the Volunteer Comments list
      const listResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/Volunteer%20Comments`
      );
      
      console.log('📋 Volunteer Comments list for adding:', listResponse);
      
      // Get the list schema to find the correct field names
      const schemaResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/columns`
      );
      
      console.log('📋 Volunteer Comments list schema for adding:', schemaResponse);
      
      // Find the volunteer lookup field
      const volunteerField = schemaResponse.value?.find((col: any) => 
        col.name.toLowerCase().includes('volunteer') || 
        col.displayName.toLowerCase().includes('volunteer')
      );
      
      console.log('🔍 Found volunteer field for adding:', volunteerField);
      
      if (!volunteerField) {
        throw new Error('Volunteer field not found in Volunteer Comments list schema');
      }
      
      // Create the comment item with the correct field name
      // Use the safe comment data approach
      console.log('🔍 Using safe comment data approach...');
      console.log('🔍 About to call createSafeCommentData with:', { volunteerId, comment, volunteerName });
      const commentData = await this.createSafeCommentData(volunteerId, comment, volunteerName);
      console.log('🔍 createSafeCommentData returned:', commentData);
      
      // Add the comment to the list
      const response = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/items`,
        {
          method: 'POST',
          body: JSON.stringify(commentData)
        }
      );
      
      console.log('✅ Comment added successfully:', response);
    } catch (error) {
      console.error('❌ Error adding volunteer comment to list:', error);
      throw error;
    }
  }

  // Add a comment to a volunteer item
  async addVolunteerComment(volunteerId: string, comment: string): Promise<void> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      // Get the volunteer list
      const listResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/True%20Care%20Clinic%20Volunteer%20List`
      );
      
      // Add comment using the activity API
      const commentData = {
        comment: comment,
        contentType: 'text'
      };
      
      await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/items/${volunteerId}/activities`,
        {
          method: 'POST',
          body: JSON.stringify({
            activity: {
              commentAction: commentData
            }
          })
        }
      );
      
      console.log('✅ Comment added successfully');
    } catch (error) {
      console.error('Error adding volunteer comment:', error);
      throw error;
    }
  }

  // Create the Volunteer Comments list if it doesn't exist
  async createVolunteerCommentsList(): Promise<void> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      // Check if the list already exists
      const listExists = await this.checkVolunteerCommentsListExists();
      if (listExists) {
        console.log('✅ Volunteer Comments list already exists');
        return;
      }
      
      console.log('📝 Creating Volunteer Comments list...');
      
      // Create the list
      const listData = {
        displayName: 'Volunteer Comments',
        description: 'Comments for volunteers',
        columns: [
          {
            name: 'Title',
            displayName: 'Title',
            type: 'text'
          },
          {
            name: 'Comment',
            displayName: 'Comment',
            type: 'note'
          },
          {
            name: 'VolunteerID',
            displayName: 'Volunteer ID',
            type: 'text'
          }
        ]
      };
      
      const response = await this.makeGraphRequest(
        `/sites/${siteId}/lists`,
        {
          method: 'POST',
          body: JSON.stringify(listData)
        }
      );
      
      console.log('✅ Volunteer Comments list created successfully:', response);
    } catch (error) {
      console.error('❌ Error creating Volunteer Comments list:', error);
      throw error;
    }
  }

  // Test method to verify comment methods are available
  async testCommentMethods(): Promise<void> {
    console.log('🧪 Testing comment methods availability...');
    console.log('✅ checkVolunteerCommentsListExists available:', typeof this.checkVolunteerCommentsListExists === 'function');
    console.log('✅ getVolunteerCommentsFromList available:', typeof this.getVolunteerCommentsFromList === 'function');
    console.log('✅ addVolunteerCommentToList available:', typeof this.addVolunteerCommentToList === 'function');
    console.log('✅ createVolunteerCommentsList available:', typeof this.createVolunteerCommentsList === 'function');
  }

  // Test method to debug Volunteer Comments list schema
  async debugVolunteerCommentsSchema(): Promise<void> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      console.log('🔍 Debugging Volunteer Comments list schema...');
      
      // Get the Volunteer Comments list
      const listResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/Volunteer%20Comments`
      );
      
      console.log('📋 Volunteer Comments list:', listResponse);
      
      // Get the list schema
      const schemaResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/columns`
      );
      
      console.log('📋 Complete schema response:', schemaResponse);
      
      if (schemaResponse.value && schemaResponse.value.length > 0) {
        console.log('📋 Available columns:');
        schemaResponse.value.forEach((col: any, index: number) => {
          console.log(`  ${index + 1}. Name: "${col.name}", DisplayName: "${col.displayName}", Type: "${col.type}"`);
        });
        
        // Try to get all items to see what fields are actually used
        const itemsResponse = await this.makeGraphRequest(
          `/sites/${siteId}/lists/${listResponse.id}/items?$top=5`
        );
        
        console.log('📋 Sample items:', itemsResponse);
        
        if (itemsResponse.value && itemsResponse.value.length > 0) {
          console.log('📋 Sample item fields:');
          console.log(itemsResponse.value[0].fields);
        }
      }
    } catch (error) {
      console.error('❌ Error debugging Volunteer Comments schema:', error);
    }
  }

  // Comprehensive debug method for Volunteer Comments functionality
  async debugVolunteerCommentsComplete(): Promise<void> {
    try {
      console.log('🔍 === VOLUNTEER COMMENTS DEBUG START ===');
      
      await this.initialize();
      const siteId = await this.getSiteId();
      console.log('✅ Site ID:', siteId);
      
      // Test 1: Check if list exists
      console.log('🔍 Test 1: Checking if Volunteer Comments list exists...');
      const listExists = await this.checkVolunteerCommentsListExists();
      console.log('✅ List exists:', listExists);
      
      if (!listExists) {
        console.log('⚠️ List does not exist, creating it...');
        await this.createVolunteerCommentsList();
        console.log('✅ List created');
      }
      
      // Test 2: Get list details
      console.log('🔍 Test 2: Getting list details...');
      const listResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/Volunteer%20Comments`
      );
      console.log('✅ List response:', listResponse);
      
      // Test 3: Get schema
      console.log('🔍 Test 3: Getting list schema...');
      const schemaResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/columns`
      );
      console.log('✅ Schema response:', schemaResponse);
      
      if (schemaResponse.value && schemaResponse.value.length > 0) {
        console.log('📋 Available columns:');
        schemaResponse.value.forEach((col: any, index: number) => {
          console.log(`  ${index + 1}. Name: "${col.name}", DisplayName: "${col.displayName}", Type: "${col.type}"`);
        });
        
        // Test 4: Find volunteer field
        console.log('🔍 Test 4: Finding volunteer field...');
        const volunteerField = schemaResponse.value.find((col: any) => 
          col.name.toLowerCase().includes('volunteer') || 
          col.displayName.toLowerCase().includes('volunteer')
        );
        console.log('✅ Found volunteer field:', volunteerField);
        
        if (volunteerField) {
          console.log('🔍 Test 5: Testing field name for filtering...');
          const fieldName = volunteerField.name;
          console.log('✅ Field name for filtering:', fieldName);
          
          // Test 6: Try to get items with this field
          console.log('🔍 Test 6: Testing item retrieval...');
          try {
            const itemsResponse = await this.makeGraphRequest(
              `/sites/${siteId}/lists/${listResponse.id}/items?$top=5`
            );
            console.log('✅ Items response:', itemsResponse);
            
            if (itemsResponse.value && itemsResponse.value.length > 0) {
              console.log('📋 Sample item fields:', itemsResponse.value[0].fields);
            }
          } catch (error) {
            console.error('❌ Error getting items:', error);
          }
        }
      }
      
      console.log('🔍 === VOLUNTEER COMMENTS DEBUG END ===');
    } catch (error) {
      console.error('❌ Error in debug method:', error);
    }
  }

  // Test method to debug adding comments
  async debugAddComment(volunteerId: string, volunteerName: string, comment: string): Promise<void> {
    try {
      console.log('🔍 === DEBUG ADD COMMENT START ===');
      console.log('📝 Input:', { volunteerId, volunteerName, comment });
      
      await this.initialize();
      const siteId = await this.getSiteId();
      
      // Get the Volunteer Comments list
      const listResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/Volunteer%20Comments`
      );
      console.log('✅ List response:', listResponse);
      
      // Get the list schema
      const schemaResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/columns`
      );
      console.log('✅ Schema response:', schemaResponse);
      
      // Find the volunteer field
      const volunteerField = schemaResponse.value?.find((col: any) => 
        col.name.toLowerCase().includes('volunteer') || 
        col.displayName.toLowerCase().includes('volunteer')
      );
      console.log('✅ Found volunteer field:', volunteerField);
      
      if (!volunteerField) {
        console.error('❌ No volunteer field found!');
        return;
      }
      
      // Use the safe comment data approach
      console.log('🔍 Using safe comment data approach...');
      console.log('🔍 About to call createSafeCommentData with:', { volunteerId, comment, volunteerName });
      const commentData = await this.createSafeCommentData(volunteerId, comment, volunteerName);
      console.log('🔍 createSafeCommentData returned:', commentData);
      
      // Try to add the comment
      console.log('🔍 Attempting to add comment...');
      const response = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/items`,
        {
          method: 'POST',
          body: JSON.stringify(commentData)
        }
      );
      
      console.log('✅ Comment added successfully:', response);
      console.log('🔍 === DEBUG ADD COMMENT END ===');
    } catch (error) {
      console.error('❌ Error in debug add comment:', error);
      console.log('🔍 === DEBUG ADD COMMENT END (WITH ERROR) ===');
    }
  }

  // Simple debug method to test if methods are available
  async debugSimple(): Promise<void> {
    console.log('🔍 === SIMPLE DEBUG START ===');
    console.log('✅ This method exists and is working!');
    console.log('🔍 === SIMPLE DEBUG END ===');
  }

  // Method to list all available methods for debugging
  async listAvailableMethods(): Promise<void> {
    console.log('🔍 === AVAILABLE METHODS ===');
    const methods = Object.getOwnPropertyNames(Object.getPrototypeOf(this));
    console.log('📋 All methods:', methods);
    
    // Check for specific debug methods
    const debugMethods = methods.filter(method => method.includes('debug'));
    console.log('🔍 Debug methods found:', debugMethods);
    
    // Check if our specific methods exist
    console.log('🔍 debugSimple exists:', typeof this.debugSimple === 'function');
    console.log('🔍 debugVolunteerCommentsComplete exists:', typeof this.debugVolunteerCommentsComplete === 'function');
    console.log('🔍 === END AVAILABLE METHODS ===');
  }

  // Method to check writable fields and create safe comment data
  async createSafeCommentData(volunteerId: string, comment: string, volunteerName?: string): Promise<any> {
    console.log('🔍 createSafeCommentData called with:', { volunteerId, comment, volunteerName });
    try {
      const siteId = await this.getSiteId();
      
      // Get the Volunteer Comments list
      const listResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/Volunteer%20Comments`
      );
      
      // Get the list schema
      const schemaResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/columns`
      );
      
      console.log('🔍 All schema fields:', schemaResponse.value.map((col: any) => ({
        name: col.name,
        displayName: col.displayName,
        readOnly: col.readOnly,
        type: col.type,
        lookup: col.lookup,
        columnGroup: col.columnGroup
      })));
      
      // Log detailed info about volunteer-related fields
      const volunteerFields = schemaResponse.value.filter((col: any) => 
        col.name.toLowerCase().includes('volunteer') || 
        col.displayName.toLowerCase().includes('volunteer')
      );
      console.log('🔍 Volunteer-related fields:', volunteerFields);
      
      // Log the full structure of each volunteer field
      volunteerFields.forEach((field: any, index: number) => {
        console.log(`🔍 Volunteer field ${index + 1} full structure:`, JSON.stringify(field, null, 2));
      });
      
      // Log writable fields for debugging
      const writableFields = schemaResponse.value.filter((col: any) => !col.readOnly);
      console.log('✅ Writable fields:', writableFields.map((col: any) => ({
        name: col.name,
        displayName: col.displayName,
        type: col.type
      })));
      
      // Create safe comment data with proper field mapping
      const safeCommentData: { fields: { [key: string]: any } } = {
        fields: {}
      };
      
      // Add comment field
      const commentField = schemaResponse.value.find((col: any) => 
        col.name === 'Comment' || col.displayName === 'Comment'
      );
      
      if (commentField) {
        safeCommentData.fields[commentField.name] = comment;
        console.log(`✅ Added comment to field: ${commentField.name}`);
      }
      
      // Handle volunteer fields - look for fields by name/displayName without type restrictions
      const volunteerNameField = schemaResponse.value.find((col: any) => 
        (col.name === 'VolunteerName' || col.displayName === 'Volunteer Name' || 
         col.name === 'Title' || col.displayName === 'Title') && 
        !col.readOnly
      );
      
      const volunteerIdField = schemaResponse.value.find((col: any) => 
        (col.name === 'Id0' || col.displayName === 'Id' || 
         col.name === 'ID' || col.displayName === 'ID' ||
         col.name === 'Id' || col.displayName === 'Id' ||
         col.name === 'VolunteerId' || col.displayName === 'Volunteer ID' ||
         col.name === 'Volunteer_x0020_Name_x003a__x002' || col.displayName === 'Volunteer Name: ID') && 
        !col.readOnly
      );
      
      console.log('🔍 Found volunteer name field:', volunteerNameField);
      console.log('🔍 Found volunteer ID field:', volunteerIdField);
      
      // Only work with writable fields
      const writableVolunteerFields = [volunteerNameField, volunteerIdField].filter(field => field && !field.readOnly);
      console.log('✅ Writable volunteer fields:', writableVolunteerFields.map(f => f?.name));
      
      // Debug: Show all fields that match our criteria
      const allVolunteerNameFields = schemaResponse.value.filter((col: any) => 
        (col.name === 'VolunteerName' || col.displayName === 'Volunteer Name' || 
         col.name === 'Title' || col.displayName === 'Title')
      );
      console.log('🔍 All volunteer name fields found:', allVolunteerNameFields.map((f: any) => ({ name: f.name, displayName: f.displayName, readOnly: f.readOnly })));
      
      const allVolunteerIdFields = schemaResponse.value.filter((col: any) => 
        (col.name === 'Id0' || col.displayName === 'Id' || 
         col.name === 'ID' || col.displayName === 'ID' ||
         col.name === 'Id' || col.displayName === 'Id' ||
         col.name === 'VolunteerId' || col.displayName === 'Volunteer ID' ||
         col.name === 'Volunteer_x0020_Name_x003a__x002' || col.displayName === 'Volunteer Name: ID')
      );
      console.log('🔍 All volunteer ID fields found:', allVolunteerIdFields.map((f: any) => ({ name: f.name, displayName: f.displayName, readOnly: f.readOnly })));
      
      // Debug: Show all available fields that might be volunteer-related
      const allTextFields = schemaResponse.value.filter((col: any) => 
        !col.readOnly
      );
      console.log('🔍 All available writable fields:', allTextFields.map((f: any) => ({ name: f.name, displayName: f.displayName })));
      
      // Debug: Show what we're trying to write
      console.log('🔍 Trying to write volunteer data:', { volunteerName, volunteerId });
      
      // Debug: Show all fields with their details
      console.log('🔍 All schema fields with details:', schemaResponse.value.map((col: any) => ({
        name: col.name,
        displayName: col.displayName,
        readOnly: col.readOnly,
        type: col.type
      })));
      
      // Set volunteer name as simple text if field is writable
      if (volunteerName && volunteerNameField && !volunteerNameField.readOnly) {
        safeCommentData.fields[volunteerNameField.name] = volunteerName;
        console.log(`✅ Added volunteer name as text: ${volunteerNameField.name} = ${volunteerName}`);
      }
      
      // Set volunteer ID as text if field is writable
      if (volunteerId && volunteerIdField && !volunteerIdField.readOnly) {
        safeCommentData.fields[volunteerIdField.name] = volunteerId;
        console.log(`✅ Added volunteer ID: ${volunteerIdField.name} = ${volunteerId}`);
      }
      
      // Fallback: Try writing directly to known field names
      if (volunteerName && !safeCommentData.fields.VolunteerName) {
        safeCommentData.fields.VolunteerName = volunteerName;
        console.log(`✅ Added volunteer name as fallback: VolunteerName = ${volunteerName}`);
      }
      
      if (volunteerId && !safeCommentData.fields.Id0) {
        safeCommentData.fields.Id0 = volunteerId;
        console.log(`✅ Added volunteer ID as fallback: Id0 = ${volunteerId}`);
      }
      
      // More aggressive fallback: Write to any field that might work
      if (volunteerName) {
        safeCommentData.fields.VolunteerName = volunteerName;
        console.log(`✅ Added volunteer name as direct write: VolunteerName = ${volunteerName}`);
      }
      
      if (volunteerId) {
        safeCommentData.fields.Id0 = volunteerId;
        console.log(`✅ Added volunteer ID as direct write: Id0 = ${volunteerId}`);
      }
      
      // Remove any read-only fields from the data before returning
      if (volunteerIdField && volunteerIdField.readOnly && safeCommentData.fields[volunteerIdField.name]) {
        delete safeCommentData.fields[volunteerIdField.name];
        console.log(`🗑️ Removed read-only field: ${volunteerIdField.name}`);
      }
      
      console.log('🔒 Safe comment data:', JSON.stringify(safeCommentData, null, 2));
      return safeCommentData;
      
    } catch (error) {
      console.error('❌ Error creating safe comment data:', error);
      throw error;
    }
  }



  // Helper function to find availability field names
  private findAvailabilityFields(fields: any): Record<string, string> {
    const availabilityFields: Record<string, string> = {};
    
    // Look for fields that contain month names and availability
    const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
    
    monthNames.forEach(month => {
      // Try different field name patterns with proper capitalization and spaces
      const possibleNames = [
        `${month.charAt(0).toUpperCase() + month.slice(1)} Availability`, // "March Availability"
        `${month.charAt(0).toUpperCase() + month.slice(1)}Availability`, // "MarchAvailability"
        `${month.charAt(0).toUpperCase() + month.slice(1)}`, // "March"
        month.toUpperCase(), // "MARCH"
        `${month}Availability`, // "marchAvailability"
        `${month} Availability`, // "march Availability"
        `${month}Avail`,
        `${month}Avail_x003f_`,
        // Also try with underscores
        `${month.charAt(0).toUpperCase() + month.slice(1)}_Availability`,
        `${month}_Availability`,
      ];
      
      for (const fieldName of possibleNames) {
        if (fields[fieldName] !== undefined) {
          availabilityFields[`${month}Availability`] = fields[fieldName];
          console.log(`✅ Found ${month} availability field: "${fieldName}" = "${fields[fieldName]}"`);
          break;
        }
      }
    });
    
    return availabilityFields;
  }

  // Helper function to find area field names
  private findAreaFields(fields: any): { primaryArea: string, secondaryArea: string } {
    const areaFields: { primaryArea: string, secondaryArea: string } = { primaryArea: '', secondaryArea: '' };
    
    // Debug: Log all field names to see what's available
    console.log('🔍 All available fields for area mapping:', Object.keys(fields));
    
    // Look for primary area fields - prioritize AssignedArea_x0028_Volunteer_x00 (Primary Area)
    const primaryAreaPatterns = [
      'AssignedArea_x0028_Volunteer_x00',
      'Primary Area',
      'PrimaryArea',
      'PrimaryRole',
      'Primary Role',
      'PrimaryInterest',
      'Primary_Area',
      'Primary_Role'
    ];
    
    // Look for secondary area fields - prioritize SecondaryArea (Secondary Area)
    const secondaryAreaPatterns = [
      'SecondaryArea',
      'Secondary Area',
      'SecondaryArea',
      'SecondaryRole',
      'Secondary Role',
      'SecondaryInterest',
      'Secondary_Area',
      'Secondary_Role'
    ];
    
    // Find primary area
    for (const pattern of primaryAreaPatterns) {
      if (fields[pattern] !== undefined && fields[pattern] !== null && fields[pattern] !== '') {
        // Clean the value - remove any concatenated values
        let cleanValue = fields[pattern].toString().trim();
        
        // If the value contains multiple areas (like "LabRegistrationIntakeProvider Support"), 
        // try to extract just the first one or the most relevant one
        if (cleanValue.includes('Lab') || cleanValue.includes('Registration') || 
            cleanValue.includes('Intake') || cleanValue.includes('Provider Support') ||
            cleanValue.includes('Liaison') || cleanValue.includes('Prayer') || 
            cleanValue.includes('Translator')) {
          
          // Try to extract individual areas
          const possibleAreas = ['Intake', 'Lab', 'Registration', 'Liaison', 'Provider Support', 'Prayer', 'Translator'];
          for (const area of possibleAreas) {
            if (cleanValue.includes(area)) {
              cleanValue = area;
              break;
            }
          }
        }
        
        areaFields.primaryArea = cleanValue;
        console.log(`✅ Found primary area field: "${pattern}" = "${fields[pattern]}" -> cleaned to "${cleanValue}"`);
        break;
      }
    }
    
    // Find secondary area
    // First, get all possible team names from DEPARTMENT_TEAMS to avoid parsing team names as clinic areas
    const allTeamNames: string[] = [];
    try {
      const { DEPARTMENT_TEAMS } = require('../types/sharepoint');
      Object.values(DEPARTMENT_TEAMS).forEach((teams) => {
        if (Array.isArray(teams)) {
          teams.forEach((team: string) => {
            if (typeof team === 'string' && !allTeamNames.includes(team)) {
              allTeamNames.push(team);
            }
          });
        }
      });
    } catch (e) {
      // If import fails, continue without team name checking
    }
    
    for (const pattern of secondaryAreaPatterns) {
      if (fields[pattern] !== undefined && fields[pattern] !== null && fields[pattern] !== '') {
        // Clean the value - remove any concatenated values
        let cleanValue = fields[pattern].toString().trim();
        
        // IMPORTANT: Check if this is a team name first (like "Registration Team") before parsing as clinic area
        const isTeamName = allTeamNames.some(team => 
          cleanValue.toLowerCase() === team.toLowerCase() || 
          cleanValue.toLowerCase().includes(team.toLowerCase())
        );
        
        // Only parse if it's NOT a team name and contains multiple areas
        if (!isTeamName && (cleanValue.includes('Lab') || cleanValue.includes('Registration') || 
            cleanValue.includes('Intake') || cleanValue.includes('Provider Support') ||
            cleanValue.includes('Liaison') || cleanValue.includes('Prayer') || 
            cleanValue.includes('Translator'))) {
          
          // Try to extract individual areas - for secondary, look for the second occurrence
          const possibleAreas = ['Intake', 'Lab', 'Registration', 'Liaison', 'Provider Support', 'Prayer', 'Translator'];
          const foundAreas = possibleAreas.filter(area => cleanValue.includes(area));
          
          if (foundAreas.length > 1) {
            // If multiple areas found, use the second one as secondary
            cleanValue = foundAreas[1] || foundAreas[0];
          } else if (foundAreas.length === 1) {
            cleanValue = foundAreas[0];
          }
        }
        // If it's a team name, keep it as-is (don't parse it)
        
        areaFields.secondaryArea = cleanValue;
        console.log(`✅ Found secondary area field: "${pattern}" = "${fields[pattern]}" -> cleaned to "${cleanValue}" (isTeamName: ${isTeamName})`);
        break;
      }
    }
    
    // If we still don't have separate values, try to split a combined field
    if (!areaFields.primaryArea && !areaFields.secondaryArea) {
      // Look for any field that might contain area information
      const areaRelatedFields = Object.keys(fields).filter(key => 
        key.toLowerCase().includes('area') || 
        key.toLowerCase().includes('role') || 
        key.toLowerCase().includes('interest')
      );
      
      console.log('🔍 Area-related fields found:', areaRelatedFields);
      
      for (const fieldName of areaRelatedFields) {
        const value = fields[fieldName];
        if (value && typeof value === 'string' && value.length > 0) {
          console.log(`🔍 Examining field "${fieldName}": "${value}"`);
          
          // Try to split the value if it contains multiple areas
          const possibleAreas = ['Intake', 'Lab', 'Registration', 'Liaison', 'Provider Support', 'Prayer', 'Translator'];
          const foundAreas = possibleAreas.filter(area => value.includes(area));
          
          if (foundAreas.length >= 2) {
            areaFields.primaryArea = foundAreas[0];
            areaFields.secondaryArea = foundAreas[1];
            console.log(`✅ Split combined field "${fieldName}": Primary="${foundAreas[0]}", Secondary="${foundAreas[1]}"`);
            break;
          } else if (foundAreas.length === 1) {
            areaFields.primaryArea = foundAreas[0];
            console.log(`✅ Found single area in field "${fieldName}": "${foundAreas[0]}"`);
            break;
          }
        }
      }
    }
    
    // Additional fallback: Look for any field that might be the primary/secondary area
    // This is for cases where the field names are completely different
    if (!areaFields.primaryArea || !areaFields.secondaryArea) {
      console.log('🔍 Trying additional fallback field detection...');
      
      // Look for fields that might contain area information with different naming patterns
      const allFields = Object.keys(fields);
      const areaKeywords = ['primary', 'secondary', 'area', 'role', 'interest'];
      
      for (const fieldName of allFields) {
        const value = fields[fieldName];
        if (value && typeof value === 'string' && value.length > 0) {
          const fieldLower = fieldName.toLowerCase();
          const valueLower = value.toLowerCase();
          
          // Check if this field name contains area-related keywords
          const hasAreaKeywords = areaKeywords.some(keyword => fieldLower.includes(keyword));
          
          if (hasAreaKeywords) {
            console.log(`🔍 Found potential area field "${fieldName}": "${value}"`);
            
            // Check if the value contains known area names
            const possibleAreas = ['Intake', 'Lab', 'Registration', 'Liaison', 'Provider Support', 'Prayer', 'Translator'];
            const foundAreas = possibleAreas.filter(area => valueLower.includes(area.toLowerCase()));
            
            if (foundAreas.length > 0) {
              if (!areaFields.primaryArea && fieldLower.includes('primary')) {
                areaFields.primaryArea = foundAreas[0];
                console.log(`✅ Found primary area in "${fieldName}": "${foundAreas[0]}"`);
              } else if (!areaFields.secondaryArea && fieldLower.includes('secondary')) {
                areaFields.secondaryArea = foundAreas[0];
                console.log(`✅ Found secondary area in "${fieldName}": "${foundAreas[0]}"`);
              } else if (!areaFields.primaryArea && !areaFields.secondaryArea) {
                // If we can't determine which is which, use the first as primary
                areaFields.primaryArea = foundAreas[0];
                if (foundAreas.length > 1) {
                  areaFields.secondaryArea = foundAreas[1];
                }
                console.log(`✅ Found areas in "${fieldName}": Primary="${foundAreas[0]}", Secondary="${foundAreas[1] || 'none'}"`);
              }
            }
          }
        }
      }
    }
    
    console.log('🔍 Final area mapping:', areaFields);
    
    // If we still don't have the correct values, try to find fields that contain the specific values we're seeing
    if (!areaFields.primaryArea || areaFields.primaryArea.includes('LabRegistrationIntakeProvider Support')) {
      console.log('🔍 Primary area still not correct, searching for fields with "Intake"...');
      
      // Look for any field that contains "Intake" specifically
      for (const fieldName of Object.keys(fields)) {
        const value = fields[fieldName];
        if (value && typeof value === 'string' && value.includes('Intake')) {
          console.log(`🔍 Found field "${fieldName}" containing "Intake": "${value}"`);
          
          // If this field contains only "Intake" or "Intake" as a separate value, use it
          if (value === 'Intake' || (value.includes('Intake') && !value.includes('LabRegistrationIntakeProvider Support'))) {
            areaFields.primaryArea = 'Intake';
            console.log(`✅ Set primary area to "Intake" from field "${fieldName}"`);
            break;
          }
        }
      }
    }
    
    if (!areaFields.secondaryArea || areaFields.secondaryArea.includes('LabRegistrationIntakeProvider Support')) {
      console.log('🔍 Secondary area still not correct, searching for fields with "Provider Support"...');
      
      // Look for any field that contains "Provider Support" specifically
      for (const fieldName of Object.keys(fields)) {
        const value = fields[fieldName];
        if (value && typeof value === 'string' && value.includes('Provider Support')) {
          console.log(`🔍 Found field "${fieldName}" containing "Provider Support": "${value}"`);
          
          // If this field contains only "Provider Support" or "Provider Support" as a separate value, use it
          if (value === 'Provider Support' || (value.includes('Provider Support') && !value.includes('LabRegistrationIntakeProvider Support'))) {
            areaFields.secondaryArea = 'Provider Support';
            console.log(`✅ Set secondary area to "Provider Support" from field "${fieldName}"`);
            break;
          }
        }
      }
    }
    
    return areaFields;
  }

  // Document Library Methods
  async getDocumentLibraryDriveId(): Promise<string> {
    try {
      const siteId = await this.getSiteId();
      const response = await this.makeGraphRequest(
        `/sites/${siteId}/drives?$filter=name eq 'Documents'`
      );
      
      if (response.value && response.value.length > 0) {
        return response.value[0].id;
      }
      
      // Fallback: try to get default document library
      const drivesResponse = await this.makeGraphRequest(`/sites/${siteId}/drives`);
      if (drivesResponse.value && drivesResponse.value.length > 0) {
        return drivesResponse.value[0].id;
      }
      
      throw new Error('Document library not found');
    } catch (error) {
      console.error('Error getting document library drive ID:', error);
      throw error;
    }
  }

  async getDriveItems(folderPath: string = ''): Promise<any[]> {
    try {
      const driveId = await this.getDocumentLibraryDriveId();
      let endpoint = `/sites/${await this.getSiteId()}/drives/${driveId}/root`;
      
      if (folderPath) {
        // Use the path-based endpoint for folders
        const encodedPath = encodeURIComponent(folderPath);
        endpoint = `/sites/${await this.getSiteId()}/drives/${driveId}/root:/${encodedPath}:`;
      }
      
      endpoint += '/children?$select=id,name,size,lastModifiedDateTime,createdDateTime,webUrl,folder,file';
      
      const response = await this.makeGraphRequest(endpoint);
      return response.value || [];
    } catch (error) {
      console.error('Error getting drive items:', error);
      throw error;
    }
  }

  async createFolder(parentPath: string, folderName: string): Promise<any> {
    try {
      const driveId = await this.getDocumentLibraryDriveId();
      let endpoint = `/sites/${await this.getSiteId()}/drives/${driveId}/root`;
      
      if (parentPath) {
        const encodedPath = encodeURIComponent(parentPath);
        endpoint = `/sites/${await this.getSiteId()}/drives/${driveId}/root:/${encodedPath}:`;
      }
      
      endpoint += '/children';
      
      const response = await this.makeGraphRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          name: folderName,
          folder: {},
          '@microsoft.graph.conflictBehavior': 'rename'
        })
      });
      
      return response;
    } catch (error) {
      console.error('Error creating folder:', error);
      throw error;
    }
  }

  async uploadFile(parentPath: string, fileName: string, fileContent: ArrayBuffer | Blob): Promise<any> {
    try {
      console.log('📤 Starting file upload:', { parentPath, fileName });
      const driveId = await this.getDocumentLibraryDriveId();
      const siteId = await this.getSiteId();
      
      // Get access token once
      const token = await this.authService.getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      // Get the target folder item ID first (similar to how we do it in moveItem)
      let targetFolderId = 'root';
      if (parentPath) {
        // Clean the path
        const cleanPath = parentPath.replace(/^\/+|\/+$/g, '');
        if (cleanPath) {
          console.log('🔍 Looking up folder:', cleanPath);
          const encodedPath = encodeURIComponent(cleanPath);
          const targetFolderEndpoint = `/sites/${siteId}/drives/${driveId}/root:/${encodedPath}:`;
          const folderUrl = `https://graph.microsoft.com/v1.0${targetFolderEndpoint}`;
          
          console.log('🔍 Folder lookup URL:', folderUrl);
          
          const folderResponse = await fetch(folderUrl, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          
          if (!folderResponse.ok) {
            const errorText = await folderResponse.text();
            console.error('❌ Failed to find target folder:', folderResponse.status, errorText);
            // If folder not found, try uploading to root instead
            console.log('⚠️ Folder not found, uploading to root instead');
            targetFolderId = 'root';
          } else {
            const folderData = await folderResponse.json();
            targetFolderId = folderData.id;
            console.log('✅ Found folder ID:', targetFolderId);
          }
        }
      }
      
      // Use the item ID approach for upload - upload to the folder's children
      const encodedFileName = encodeURIComponent(fileName);
      const endpoint = `/sites/${siteId}/drives/${driveId}/items/${targetFolderId}:/${encodedFileName}:/content`;
      const fullUrl = `https://graph.microsoft.com/v1.0${endpoint}`;
      
      console.log('📤 Uploading file to:', fullUrl);
      console.log('📤 Upload details:', { targetFolderId, fileName, encodedFileName });
      
      const response = await fetch(fullUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/octet-stream'
        },
        body: fileContent
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Upload failed:', response.status, errorText);
        throw new Error(`Upload failed: ${response.status} ${errorText}`);
      }
      
      const result = await response.json();
      console.log('✅ File uploaded successfully:', result);
      return result;
    } catch (error) {
      console.error('❌ Error uploading file:', error);
      throw error;
    }
  }

  async downloadFile(itemId: string): Promise<Blob> {
    try {
      const driveId = await this.getDocumentLibraryDriveId();
      const endpoint = `/sites/${await this.getSiteId()}/drives/${driveId}/items/${itemId}/content`;
      
      const token = await this.authService.getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      const response = await fetch(`https://graph.microsoft.com/v1.0${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }
      
      return await response.blob();
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  }

  async moveItem(itemId: string, sourcePath: string, targetPath: string, itemName: string): Promise<void> {
    try {
      const driveId = await this.getDocumentLibraryDriveId();
      const siteId = await this.getSiteId();
      
      // Get the target folder ID
      let targetFolderId = 'root';
      if (targetPath) {
        const encodedPath = encodeURIComponent(targetPath);
        const targetFolderEndpoint = `/sites/${siteId}/drives/${driveId}/root:/${encodedPath}:`;
        
        const token = await this.authService.getAccessToken();
        if (!token) {
          throw new Error('No access token available');
        }
        
        const folderResponse = await fetch(`https://graph.microsoft.com/v1.0${targetFolderEndpoint}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (!folderResponse.ok) {
          throw new Error(`Failed to find target folder: ${folderResponse.status}`);
        }
        
        const folderData = await folderResponse.json();
        targetFolderId = folderData.id;
      }
      
      // Get the current item location
      const currentItemEndpoint = `/sites/${siteId}/drives/${driveId}/items/${itemId}`;
      
      // Get access token
      const token = await this.authService.getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      // Move the item using PATCH with parentReference
      const response = await fetch(`https://graph.microsoft.com/v1.0${currentItemEndpoint}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parentReference: {
            id: targetFolderId
          },
          name: itemName
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to move item: ${response.status} ${errorText}`);
      }
      
      console.log('✅ Item moved successfully');
    } catch (error) {
      console.error('Error moving item:', error);
      throw error;
    }
  }

  async deleteItem(itemId: string): Promise<void> {
    try {
      const driveId = await this.getDocumentLibraryDriveId();
      const endpoint = `/sites/${await this.getSiteId()}/drives/${driveId}/items/${itemId}`;
      
      const token = await this.authService.getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      const url = `https://graph.microsoft.com/v1.0${endpoint}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Delete request failed: ${response.status}`, errorText);
        
        if (response.status === 404) {
          throw new Error('Item not found. It may have already been deleted.');
        }
        
        throw new Error(`Failed to delete item: ${response.status} ${errorText}`);
      }
      
      // DELETE requests typically return 204 No Content (empty response)
      // 204 is success, so we don't need to parse JSON
      console.log('✅ Item deleted successfully');
    } catch (error) {
      console.error('Error deleting item:', error);
      throw error;
    }
  }

  async ensureDepartmentFoldersExist(): Promise<void> {
    try {
      const departments = [
        'Clinical',
        'Marketing & Communications',
        'Operations',
        'Finance & Development',
        'Legal & HR',
        'Care',
        'Technology'
      ];
      
      const existingItems = await this.getDriveItems('');
      // Deduplicate by ID first
      const uniqueItems = existingItems.reduce((acc: any[], item: any) => {
        if (!acc.find(existing => existing.id === item.id)) {
          acc.push(item);
        }
        return acc;
      }, []);
      
      // Case-insensitive comparison for folder names
      const existingFolders = uniqueItems
        .filter(item => item.folder)
        .map(item => item.name.toLowerCase());
      
      for (const department of departments) {
        const departmentLower = department.toLowerCase();
        if (!existingFolders.includes(departmentLower)) {
          try {
            await this.createFolder('', department);
            console.log(`✅ Created folder: ${department}`);
            // Small delay to avoid rate limiting
            await new Promise(resolve => setTimeout(resolve, 200));
          } catch (error: any) {
            // Check if error is because folder already exists (might be a timing issue)
            if (error.message && error.message.includes('already exists')) {
              console.log(`ℹ️ Folder ${department} already exists (detected during creation)`);
            } else {
              console.error(`❌ Error creating folder ${department}:`, error);
            }
            // Continue with other folders even if one fails
          }
        } else {
          console.log(`ℹ️ Folder already exists: ${department}`);
        }
      }
    } catch (error) {
      console.error('Error ensuring department folders exist:', error);
      // Don't throw - allow the page to load even if folder creation fails
    }
  }

  // ============================================
  // Department Directors Management
  // ============================================

  /**
   * Get all department directors from the Department Directors list
   * Returns an array of directors, supporting multiple directors per department
   */
  async getDepartmentDirectors(): Promise<Array<{
    id: string;
    department: string;
    directorName: string;
    directorEmail?: string;
    assignedBy: string;
    assignedDate: string;
    isActive: boolean;
  }>> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();

      // Get the Department Directors list
      const listResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/Department%20Directors`
      );

      // Get all directors (fetch all items, filter in code - same pattern as getVolunteers)
      // NOTE: No $filter in query - we fetch all items and filter in JavaScript to avoid non-indexed field errors
      console.log('🔍 Fetching all department directors (no filter in query, matching getVolunteers pattern)');
      const itemsResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/items?$expand=fields&$top=5000`
      );

      if (!itemsResponse.value || itemsResponse.value.length === 0) {
        console.log('No department directors found in SharePoint');
        return [];
      }

      // Filter active directors in code (since IsActive is not indexed)
      const activeDirectors = itemsResponse.value.filter((item: any) => {
        const fields = item.fields || {};
        return fields.IsActive !== false;
      });

      console.log(`Found ${activeDirectors.length} active department directors out of ${itemsResponse.value.length} total`);

      if (activeDirectors.length === 0) {
        return [];
      }

      return activeDirectors.map((item: any) => {
        const fields = item.fields || {};
        // Handle both "DirectorName" (internal name) and "Director Name" (display name) or "Director" (if lookup)
        const directorName = fields.DirectorName || fields['Director Name'] || fields.Director || '';
        return {
          id: item.id,
          department: fields.Department || '',
          directorName: directorName,
          directorEmail: fields.DirectorEmail || fields['Director Email'] || '',
          assignedBy: fields.AssignedBy?.email || fields.AssignedBy || '',
          assignedDate: fields.AssignedDate || fields['Assigned Date'] || '',
          isActive: fields.IsActive !== false, // Default to true if not set
        };
      });
    } catch (error) {
      console.error('Error getting department directors:', error);
      // Return empty array on error - fallback to hardcoded values
      return [];
    }
  }

  /**
   * Get directors for a specific department
   * Returns array of director names (supports multiple directors)
   */
  async getDirectorsForDepartment(department: string): Promise<string[]> {
    try {
      const allDirectors = await this.getDepartmentDirectors();
      const normalizedDept = this.normalizeDepartmentName(department);
      
      return allDirectors
        .filter(d => this.normalizeDepartmentName(d.department) === normalizedDept)
        .map(d => d.directorName)
        .filter(name => name); // Remove empty names
    } catch (error) {
      console.error('Error getting directors for department:', error);
      return [];
    }
  }

  /**
   * Add a new department director
   */
  async addDepartmentDirector(
    department: string,
    directorName: string,
    directorEmail?: string
  ): Promise<void> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();

      // Get current user
      const currentUser = await this.authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // Get the Department Directors list
      const listResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/Department%20Directors`
      );

      // Create the new item
      const itemData: any = {
        fields: {
          Department: department,
          DirectorName: directorName,
          IsActive: true,
          AssignedDate: new Date().toISOString(),
        },
      };

      if (directorEmail) {
        itemData.fields.DirectorEmail = directorEmail;
      }

      // Set AssignedBy - need to get user ID from Graph API
      try {
        const userResponse = await this.makeGraphRequest('/me');
        itemData.fields.AssignedBy = userResponse.id;
      } catch {
        // Fallback to email if ID lookup fails
        itemData.fields.AssignedBy = currentUser.email || currentUser.name;
      }

      await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/items`,
        {
          method: 'POST',
          body: JSON.stringify(itemData),
        }
      );
    } catch (error) {
      console.error('Error adding department director:', error);
      throw error;
    }
  }

  /**
   * Update a department director
   */
  async updateDepartmentDirector(
    directorId: string,
    updates: {
      department?: string;
      directorName?: string;
      directorEmail?: string;
      isActive?: boolean;
    }
  ): Promise<void> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();

      // Get the Department Directors list
      const listResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/Department%20Directors`
      );

      const itemData: any = {
        fields: {},
      };

      if (updates.department !== undefined) itemData.fields.Department = updates.department;
      if (updates.directorName !== undefined) itemData.fields.DirectorName = updates.directorName;
      if (updates.directorEmail !== undefined) itemData.fields.DirectorEmail = updates.directorEmail;
      if (updates.isActive !== undefined) itemData.fields.IsActive = updates.isActive;

      await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/items/${directorId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(itemData),
        }
      );
    } catch (error) {
      console.error('Error updating department director:', error);
      throw error;
    }
  }

  /**
   * Remove/deactivate a department director
   */
  async removeDepartmentDirector(directorId: string): Promise<void> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();

      // Get the Department Directors list
      const listResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/Department%20Directors`
      );

      // Soft delete by setting IsActive to false (same pattern as updateVolunteer)
      await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/items/${directorId}/fields`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            IsActive: false,
          }),
        }
      );

      console.log('✅ Successfully removed department director (set IsActive to false)');
      
      // Clear cache since data was updated
      this.clearCache();
    } catch (error) {
      console.error('Error removing department director:', error);
      throw error;
    }
  }

  /**
   * Normalize department name for comparison
   */
  private normalizeDepartmentName(dept: string): string {
    if (!dept) return '';
    return dept.trim();
  }
}

// Version marker to force cache refresh when implementation changes
const SHAREPOINT_SERVICE_VERSION = '2.0.0'; // Increment when getDepartmentDirectors implementation changes

export const getSharePointService = (authService?: AuthService, forceRefresh: boolean = false): SharePointService => {
  // Use the global instance that was created in App.tsx
  if (typeof window !== 'undefined' && (window as any).sharePointService && !forceRefresh) {
    const service = (window as any).sharePointService;
    
    // Check if the service has the correct version and all required methods
    const serviceVersion = (window as any).sharePointServiceVersion;
    if (serviceVersion === SHAREPOINT_SERVICE_VERSION &&
        typeof service.getDepartmentDirectors === 'function' && 
        typeof service.addDepartmentDirector === 'function' &&
        typeof service.removeDepartmentDirector === 'function') {
      console.log('✅ Using cached SharePoint service (version', serviceVersion, ')');
      return service;
    } else {
      console.log('🔄 Cached SharePoint service version mismatch or missing methods, recreating...');
      console.log('   Expected version:', SHAREPOINT_SERVICE_VERSION, 'Found:', serviceVersion);
      // Clear the cached instance and create a new one
      delete (window as any).sharePointService;
      delete (window as any).sharePointServiceVersion;
    }
  }
  
  // Create a new instance
  console.log('🔄 Creating new SharePoint service instance (version', SHAREPOINT_SERVICE_VERSION, ')');
  const service = authService || getAuthService();
  const newService = new SharePointService(service);
  
  // Cache the new instance globally with version marker
  if (typeof window !== 'undefined') {
    (window as any).sharePointService = newService;
    (window as any).sharePointServiceVersion = SHAREPOINT_SERVICE_VERSION;
  }
  
  console.log('✅ New SharePoint service created with methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(newService)));
  return newService;
};

// Make it available globally for browser console testing
if (typeof window !== 'undefined') {
  (window as any).testSharePoint = async () => {
    try {
      // Get the SharePoint service from the React app context
      const sharePointService = (window as any).sharePointService;
      if (sharePointService) {
        await sharePointService.manualTest();
      } else {
        console.log('SharePoint service not available. Please run this from within the app.');
      }
    } catch (error) {
      console.error('Error running manual test:', error);
    }
  };
}

// Prayer Box List Operations
export const getPrayers = async (): Promise<PrayerItem[]> => {
  try {
    console.log('🔄 Starting getPrayers function...');
    const authService = getAuthService();
    const sharePointService = getSharePointService(authService);
    
    // Get the site ID
    console.log('🔍 Getting site ID...');
    const siteId = await sharePointService.getSiteId();
    console.log('📍 Site ID:', siteId);
    
    // Fetch prayers from the PrayerBox list using Graph API
    console.log('📥 Fetching prayers from SharePoint...');
    const response = await sharePointService.makeGraphRequest(
      `/sites/${siteId}/lists/PrayerBox/items?$expand=fields`
    );
    
    console.log('📋 Raw SharePoint response:', response);
    console.log('📋 Response value length:', response.value?.length || 0);
    if (response.value && response.value.length > 0) {
      console.log('📋 First item from SharePoint:', response.value[0]);
      console.log('📋 First item fields:', response.value[0].fields);
    }
    
    // Get comment counts for all prayers
    const commentCounts = await getAllPrayerCommentCounts();
    
    // Transform the response to match our PrayerItem interface
    const prayers: PrayerItem[] = response.value.map((item: any) => ({
      Id: item.id,
      Title: item.fields.Title || '',
      PrayerText: item.fields.PrayerText || '',
      Created: item.fields.Created || new Date().toISOString(),
      CreatedBy: item.fields.CreatedBy?.Title || 'Unknown',
      AssignedTo: item.fields.AssignedTo?.Title || '',
      CommentCount: commentCounts[item.id] || 0 // Use actual comment count from Prayer Comments list
    }));
    
    // Sort prayers by creation date (newest first) in JavaScript
    prayers.sort((a, b) => new Date(b.Created).getTime() - new Date(a.Created).getTime());
    
    console.log('✅ Prayers fetched successfully:', prayers.length);
    return prayers;
    
  } catch (error) {
    console.error('❌ Error fetching prayers:', error);
    console.log('🔄 Falling back to mock data...');
    // Return mock data as fallback
    const mockPrayers = [
      {
        Id: 1,
        Title: 'Prayer for Patient Recovery',
        PrayerText: 'Please pray for the recovery of our patient who is going through a difficult time.',
        Created: new Date().toISOString(),
        CreatedBy: 'Dr. Smith',
        AssignedTo: 'Prayer Team',
        CommentCount: 2
      },
      {
        Id: 2,
        Title: 'Strength for Medical Staff',
        PrayerText: 'Praying for strength and wisdom for our medical staff as they care for patients.',
        Created: new Date(Date.now() - 86400000).toISOString(),
        CreatedBy: 'Nurse Johnson',
        AssignedTo: '',
        CommentCount: 1
      }
    ];
    console.log('📋 Mock prayers created:', mockPrayers);
    return mockPrayers;
  }
};

export const addPrayer = async (prayerData: {
  title: string;
  prayerText: string;
  assignedTo?: string;
}): Promise<ItemAddResult> => {
  try {
    const authService = getAuthService();
    const sharePointService = getSharePointService(authService);
    
    // Get the site ID
    const siteId = await sharePointService.getSiteId();
    
    // Prepare the prayer data for SharePoint
    const prayerItem = {
      Title: prayerData.title,
      PrayerText: prayerData.prayerText,
      AssignedTo: prayerData.assignedTo || null,
      IsAnswered: false
    };
    
    // Add the prayer to the PrayerBox list using Graph API
    const response = await sharePointService.makeGraphRequest(
      `/sites/${siteId}/lists/PrayerBox/items`,
      {
        method: 'POST',
        body: JSON.stringify({
          fields: prayerItem
        })
      }
    );
    
    console.log('✅ Prayer added successfully:', response);
    
    return {
      data: response,
      item: response
    };
    
  } catch (error) {
    console.error('❌ Error adding prayer:', error);
    throw new Error('Failed to submit prayer. Please try again.');
  }
};

// Prayer Comments List Operations
export const getPrayerComments = async (prayerId: number): Promise<PrayerComment[]> => {
  try {
    console.log('🔄 Getting comments for prayer ID:', prayerId);
    const authService = getAuthService();
    const sharePointService = getSharePointService(authService);
    
    // Get the site ID
    const siteId = await sharePointService.getSiteId();
    
    // Work with existing Prayer Comments list - no creation needed
    console.log('ℹ️ Using existing Prayer Comments list');
    
    // First, let's try to get the list schema to understand the field names
    let correctFieldName: string | null = null;
    try {
      const schemaResponse = await sharePointService.makeGraphRequest(
        `/sites/${siteId}/lists/Prayer%20Comments/columns`
      );
      console.log('📋 Prayer Comments list schema:', schemaResponse);
      
      // Look for the prayer lookup field - check for various possible field names
      const prayerField = schemaResponse.value?.find((col: any) => 
        col.name === 'PrayerId' || 
        col.displayName === 'PrayerId' ||
        col.name === 'PrayerId_x0020_ID' ||
        col.displayName === 'PrayerId: ID' ||
        col.name === 'PrayerId_x0020_Title' ||
        col.displayName === 'PrayerId: Title' ||
        col.name.toLowerCase().includes('prayer') || 
        col.displayName.toLowerCase().includes('prayer')
      );
      console.log('🔍 Found prayer field:', prayerField);
      
      if (prayerField) {
        correctFieldName = prayerField.name;
        console.log('✅ Using correct field name from schema:', correctFieldName);
      }
    } catch (schemaError) {
      console.log('⚠️ Could not fetch schema, proceeding with default field names');
    }
    
    // If we found the correct field name from schema, use it; otherwise try variations
    const fieldNameVariations = correctFieldName 
      ? [correctFieldName] 
      : ['PrayerId', 'PrayerId_x0020_ID', 'PrayerId_x0020_Title', 'Prayer', 'PrayerLookup', 'Prayer_x0020_Id', 'Prayer_x0020_Lookup'];
    let comments: PrayerComment[] = [];
    
    for (const fieldName of fieldNameVariations) {
      try {
        console.log(`🔄 Trying field name: ${fieldName}`);
        
        // Fetch comments from the Prayer Comments list using Graph API
        // Filter by prayer lookup field - allow non-indexed queries
        const response = await sharePointService.makeGraphRequest(
          `/sites/${siteId}/lists/Prayer%20Comments/items?$expand=fields&$filter=fields/${fieldName} eq ${prayerId}`,
          {},
          true // Allow non-indexed queries
        );
        
        // Check if the list exists and has data
        if (response.value && response.value.length > 0) {
          console.log(`✅ Found comments using field name: ${fieldName}`);
          
          // Transform the response to match our PrayerComment interface
          comments = response.value.map((item: any) => ({
            Id: item.id,
            CommentText: item.fields.CommentText || item.fields.CommentText_x0020_Text || '',
            Created: item.fields.Created || new Date().toISOString(),
            CreatedBy: {
              Title: item.fields.CreatedBy?.Title || item.fields.CreatedBy?.DisplayName || 'Unknown',
              Id: item.fields.CreatedBy?.Id || 0
            }
          }));
          
          // Sort comments by creation date (newest first)
          comments.sort((a, b) => new Date(b.Created).getTime() - new Date(a.Created).getTime());
          
          console.log('✅ Comments fetched successfully:', comments.length);
          return comments;
        }
      } catch (error) {
        console.log(`❌ Failed with field name ${fieldName}:`, error);
        continue;
      }
    }
    
    console.log('📋 No comments found with any field name variation');
    
    // Check if the list exists at all
    try {
      const listExists = await sharePointService.makeGraphRequest(
        `/sites/${siteId}/lists/Prayer%20Comments`,
        {},
        true
      );
      
      if (!listExists) {
        console.log('⚠️ Prayer Comments list does not exist');
        return [];
      }
    } catch (listError) {
      console.log('⚠️ Could not verify if Prayer Comments list exists:', listError);
      return [];
    }
    
    // As a last resort, try to get all comments and filter client-side
    try {
      console.log('🔄 Trying to get all comments and filter client-side...');
      const allCommentsResponse = await sharePointService.makeGraphRequest(
        `/sites/${siteId}/lists/Prayer%20Comments/items?$expand=fields`,
        {},
        true
      );
      
      if (allCommentsResponse.value && allCommentsResponse.value.length > 0) {
        console.log('📋 Found all comments, filtering client-side...');
        
        // Filter comments that have any prayer-related field matching our prayerId
        const filteredComments = allCommentsResponse.value.filter((item: any) => {
          const fields = item.fields;
          console.log('🔍 Checking item fields:', fields);
          console.log('🔍 All field names:', Object.keys(fields));
          
          // Check various possible field names and formats
          const prayerFieldMatches = [
            fields.PrayerId,
            fields.PrayerId_x0020_ID,
            fields.PrayerId_x0020_Title,
            fields.Prayer,
            fields.PrayerLookup,
            fields.PrayerId_x0020_Title,
            fields.Prayer_x0020_Id,
            fields.Prayer_x0020_Lookup
          ].some(field => {
            console.log(`🔍 Checking field value: ${field} against prayerId: ${prayerId}`);
            if (field === prayerId) return true;
            if (field && field.toString() === prayerId.toString()) return true;
            // Handle case where field might be stored as a string number
            if (field && parseInt(field.toString()) === prayerId) return true;
            // Handle case where field might be an object with Id property
            if (field && typeof field === 'object' && field.Id === prayerId) return true;
            if (field && typeof field === 'object' && field.Id && parseInt(field.Id.toString()) === prayerId) return true;
            // Handle case where field might be stored as a lookup value
            if (field && typeof field === 'object' && field.LookupId === prayerId) return true;
            if (field && typeof field === 'object' && field.LookupId && parseInt(field.LookupId.toString()) === prayerId) return true;
            return false;
          });
          
          // Also check all fields for any that might contain prayer-related data
          const allFieldsContainPrayer = Object.keys(fields).some(fieldName => {
            const fieldValue = fields[fieldName];
            if (fieldName.toLowerCase().includes('prayer') && fieldValue) {
              console.log(`🔍 Found prayer-related field: ${fieldName} = ${fieldValue}`);
              // Check if this field value matches our prayerId
              if (fieldValue === prayerId) return true;
              if (fieldValue.toString() === prayerId.toString()) return true;
              if (parseInt(fieldValue.toString()) === prayerId) return true;
              if (typeof fieldValue === 'object' && fieldValue.Id === prayerId) return true;
              if (typeof fieldValue === 'object' && fieldValue.LookupId === prayerId) return true;
            }
            return false;
          });
          
          // Check if the title contains the prayer ID (fallback for comments created before PrayerId field was properly set)
          const titleContainsPrayerId = fields.Title && fields.Title.includes(`Prayer ${prayerId}`);
          
          const matches = prayerFieldMatches || allFieldsContainPrayer || titleContainsPrayerId;
          
          console.log(`🔍 Prayer field matches for prayer ${prayerId}:`, matches);
          console.log(`🔍 Title contains prayer ID: ${titleContainsPrayerId}`);
          return matches;
        });
        
        if (filteredComments.length > 0) {
          console.log(`✅ Found ${filteredComments.length} comments after client-side filtering`);
          
          // Transform the response to match our PrayerComment interface
          comments = filteredComments.map((item: any) => ({
            Id: item.id,
            CommentText: item.fields.CommentText || item.fields.CommentText_x0020_Text || '',
            Created: item.fields.Created || new Date().toISOString(),
            CreatedBy: {
              Title: item.fields.CreatedBy?.Title || item.fields.CreatedBy?.DisplayName || 'Unknown',
              Id: item.fields.CreatedBy?.Id || 0
            }
          }));
          
          // Sort comments by creation date (newest first)
          comments.sort((a, b) => new Date(b.Created).getTime() - new Date(a.Created).getTime());
          
          console.log('✅ Comments fetched successfully via client-side filtering:', comments.length);
          return comments;
        }
      }
    } catch (fallbackError) {
      console.log('❌ Client-side filtering also failed:', fallbackError);
    }
    
    return [];
    
  } catch (error) {
    console.error('❌ Error fetching prayer comments:', error);
    // Return mock data as fallback
    return [
      {
        Id: 1,
        CommentText: 'Praying for this patient. God is in control.',
        Created: new Date().toISOString(),
        CreatedBy: {
          Title: 'Prayer Team Member',
          Id: 1
        }
      },
      {
        Id: 2,
        CommentText: 'Adding this to our daily prayer list.',
        Created: new Date(Date.now() - 3600000).toISOString(),
        CreatedBy: {
          Title: 'Pastor Mike',
          Id: 2
        }
      }
    ];
  }
};

export const addPrayerComment = async (prayerId: number, commentText: string): Promise<ItemAddResult> => {
  try {
    console.log('🔄 Adding comment for prayer ID:', prayerId);
    const authService = getAuthService();
    const sharePointService = getSharePointService(authService);
    
    // Get the site ID
    const siteId = await sharePointService.getSiteId();
    
    // Work with existing Prayer Comments list - no creation needed
    console.log('ℹ️ Using existing Prayer Comments list for adding comment');
    
    // First, let's try to get the list schema to understand the field names
    let correctFieldName: string = 'PrayerId'; // Default fallback
    try {
      const schemaResponse = await sharePointService.makeGraphRequest(
        `/sites/${siteId}/lists/Prayer%20Comments/columns`
      );
      console.log('📋 Prayer Comments list schema for adding comment:', schemaResponse);
      
      // Look for the prayer lookup field - check for various possible field names
      const prayerField = schemaResponse.value?.find((col: any) => 
        col.name === 'PrayerId' || 
        col.displayName === 'PrayerId' ||
        col.name === 'PrayerId_x0020_ID' ||
        col.displayName === 'PrayerId: ID' ||
        col.name === 'PrayerId_x0020_Title' ||
        col.displayName === 'PrayerId: Title' ||
        col.name.toLowerCase().includes('prayer') || 
        col.displayName.toLowerCase().includes('prayer')
      );
      console.log('🔍 Found prayer field for adding comment:', prayerField);
      
      if (prayerField) {
        correctFieldName = prayerField.name;
        console.log('✅ Using correct field name for adding comment:', correctFieldName);
      }
    } catch (schemaError) {
      console.log('⚠️ Could not fetch schema for adding comment, using default field name');
    }
    
    // Prepare the comment data for SharePoint
    const commentItem = {
      Title: `Comment for Prayer ${prayerId}`, // Title is required
      [correctFieldName]: prayerId, // Use the correct field name for PrayerId
      CommentText: commentText
    };
    
    console.log('📝 Prepared comment item:', commentItem);
    console.log('📝 PrayerId being set to:', prayerId);
    console.log('📝 Field name being used:', correctFieldName);
    
    // Add the comment to the Prayer Comments list using Graph API
    const response = await sharePointService.makeGraphRequest(
      `/sites/${siteId}/lists/Prayer%20Comments/items`,
      {
        method: 'POST',
        body: JSON.stringify({
          fields: commentItem
        })
      }
    );
    
    console.log('✅ Comment added successfully:', response);
    
    return {
      data: response,
      item: response
    };
    
  } catch (error) {
    console.error('❌ Error adding prayer comment:', error);
    throw new Error('Failed to submit comment. Please try again.');
  }
};

// Get all prayer comment counts in batch
export const getAllPrayerCommentCounts = async (): Promise<Record<number, number>> => {
  try {
    console.log('🔄 Getting all prayer comment counts...');
    const authService = getAuthService();
    const sharePointService = getSharePointService(authService);
    
    // Get the site ID
    const siteId = await sharePointService.getSiteId();
    
    // Get all comments from the Prayer Comments list - allow non-indexed queries
    const response = await sharePointService.makeGraphRequest(
      `/sites/${siteId}/lists/Prayer%20Comments/items?$expand=fields($select=PrayerId)`,
      {},
      true // Allow non-indexed queries
    );
    
    // Check if the list exists and has data
    if (!response.value) {
      console.log('📋 No comments found or list does not exist');
      return {};
    }
    
    // Count comments per prayer ID
    const commentCounts: Record<number, number> = {};
    response.value.forEach((item: any) => {
      const prayerId = item.fields.PrayerId;
      let prayerIdNumber: number | null = null;
      
      if (prayerId) {
        // Handle different data types for PrayerId
        prayerIdNumber = typeof prayerId === 'number' ? prayerId : parseInt(prayerId.toString());
        if (!isNaN(prayerIdNumber)) {
          commentCounts[prayerIdNumber] = (commentCounts[prayerIdNumber] || 0) + 1;
        }
      } else {
        // Fallback: try to extract prayer ID from title for comments created before PrayerId field was properly set
        const title = item.fields.Title;
        if (title && title.includes('Prayer ')) {
          const match = title.match(/Prayer (\d+)/);
          if (match) {
            prayerIdNumber = parseInt(match[1]);
            if (!isNaN(prayerIdNumber)) {
              commentCounts[prayerIdNumber] = (commentCounts[prayerIdNumber] || 0) + 1;
            }
          }
        }
      }
    });
    
    console.log('✅ Comment counts fetched successfully:', commentCounts);
    return commentCounts;
    
  } catch (error) {
    console.error('❌ Error fetching prayer comment counts:', error);
    return {};
  }
};

// Test function for prayer comments integration
export const testPrayerCommentsIntegration = async (): Promise<void> => {
  try {
    console.log('🧪 Testing prayer comments integration...');
    const authService = getAuthService();
    const sharePointService = getSharePointService(authService);
    
    // Get the site ID
    const siteId = await sharePointService.getSiteId();
    
    // First, let's debug the actual schema
    try {
      console.log('🔍 Debugging Prayer Comments list schema...');
      const schemaResponse = await sharePointService.makeGraphRequest(
        `/sites/${siteId}/lists/Prayer%20Comments/columns`
      );
      console.log('📋 Complete Prayer Comments list schema:', JSON.stringify(schemaResponse, null, 2));
      
      // Also try to get a few sample items to see the actual field names
      const sampleItemsResponse = await sharePointService.makeGraphRequest(
        `/sites/${siteId}/lists/Prayer%20Comments/items?$top=3&$expand=fields`
      );
      console.log('📋 Sample Prayer Comments items:', JSON.stringify(sampleItemsResponse, null, 2));
      
    } catch (schemaError) {
      console.log('⚠️ Could not fetch schema for debugging:', schemaError);
    }
    
    // Test getting comments for a prayer
    const comments = await getPrayerComments(1);
    console.log('📋 Test comments:', comments);
    
    // Test getting all comment counts
    const commentCounts = await getAllPrayerCommentCounts();
    console.log('📊 Test comment counts:', commentCounts);
    
    // Test the title parsing logic specifically
    console.log('🧪 Testing title parsing logic...');
    const testTitles = [
      'Comment for Prayer 2',
      'Comment for Prayer 5',
      'Some other comment',
      'Prayer 3 comment'
    ];
    
    testTitles.forEach(title => {
      const match = title.match(/Prayer (\d+)/);
      if (match) {
        console.log(`✅ Title "${title}" matches prayer ID: ${match[1]}`);
      } else {
        console.log(`❌ Title "${title}" does not match prayer pattern`);
      }
    });
    
    console.log('✅ Prayer comments integration test completed');
    
  } catch (error) {
    console.error('❌ Prayer comments integration test failed:', error);
  }
};

// Debug function to expose to window for browser console access
export const debugPrayerCommentsSchema = async (): Promise<void> => {
  try {
    console.log('🔍 Debugging Prayer Comments schema from browser console...');
    const authService = getAuthService();
    const sharePointService = getSharePointService(authService);
    
    // Get the site ID
    const siteId = await sharePointService.getSiteId();
    
    // Get the schema
    const schemaResponse = await sharePointService.makeGraphRequest(
      `/sites/${siteId}/lists/Prayer%20Comments/columns`
    );
    console.log('📋 Prayer Comments list schema:', schemaResponse);
    
    // Get sample items
    const sampleItemsResponse = await sharePointService.makeGraphRequest(
      `/sites/${siteId}/lists/Prayer%20Comments/items?$top=3&$expand=fields`
    );
    console.log('📋 Sample Prayer Comments items:', sampleItemsResponse);
    
    // Make it available globally for easy access
    (window as any).prayerCommentsSchema = schemaResponse;
    (window as any).prayerCommentsSampleItems = sampleItemsResponse;
    
    console.log('✅ Schema debug completed. Check window.prayerCommentsSchema and window.prayerCommentsSampleItems');
    
  } catch (error) {
    console.error('❌ Prayer comments schema debug failed:', error);
  }
};

// Debug function to understand Prayer Comments list structure
export const debugPrayerCommentsStructure = async (): Promise<void> => {
  try {
    console.log('🔍 Debugging Prayer Comments list structure...');
    const authService = getAuthService();
    const sharePointService = getSharePointService(authService);
    
    const siteId = await sharePointService.getSiteId();
    
    // Get the list schema
    const schemaResponse = await sharePointService.makeGraphRequest(
      `/sites/${siteId}/lists/Prayer%20Comments/columns`
    );
    console.log('📋 Prayer Comments list schema:', schemaResponse);
    
    // Get all items to see the actual data structure
    const allItemsResponse = await sharePointService.makeGraphRequest(
      `/sites/${siteId}/lists/Prayer%20Comments/items?$expand=fields`
    );
    console.log('📋 All Prayer Comments items:', allItemsResponse);
    
    if (allItemsResponse.value && allItemsResponse.value.length > 0) {
      console.log('📋 First item fields:', allItemsResponse.value[0].fields);
      console.log('📋 All field names:', Object.keys(allItemsResponse.value[0].fields));
    }
    
  } catch (error) {
    console.error('❌ Error debugging Prayer Comments structure:', error);
  }
};

// Expose debug function to window for easy access from browser console
if (typeof window !== 'undefined') {
  (window as any).debugPrayerCommentsSchema = debugPrayerCommentsSchema;
  (window as any).testPrayerCommentsIntegration = testPrayerCommentsIntegration;
  (window as any).debugPrayerCommentsStructure = debugPrayerCommentsStructure;
}

// Debug function to inspect Prayer Comments field values
export const debugPrayerCommentsFields = async (): Promise<void> => {
  try {
    console.log('🔍 Debugging Prayer Comments field values...');
    const authService = getAuthService();
    const sharePointService = getSharePointService(authService);
    
    // Get the site ID
    const siteId = await sharePointService.getSiteId();
    
    // Get all comments with their fields
    const response = await sharePointService.makeGraphRequest(
      `/sites/${siteId}/lists/Prayer%20Comments/items?$expand=fields`,
      {},
      true
    );
    
    if (response.value && response.value.length > 0) {
      console.log('📋 All Prayer Comments items with fields:');
      response.value.forEach((item: any, index: number) => {
        console.log(`Item ${index + 1}:`, {
          id: item.id,
          title: item.fields.Title,
          fields: item.fields
        });
      });
    } else {
      console.log('📋 No Prayer Comments found');
    }
    
  } catch (error) {
    console.error('❌ Error debugging Prayer Comments fields:', error);
  }
};



// Debug function to show all prayer comments and their field values
export const debugPrayerComments = async (): Promise<void> => {
  try {
    console.log('🔍 === DEBUG PRAYER COMMENTS START ===');
    const authService = getAuthService();
    const sharePointService = getSharePointService(authService);
    
    // Get the site ID
    const siteId = await sharePointService.getSiteId();
    
    // Get all comments from the Prayer Comments list
    const allCommentsResponse = await sharePointService.makeGraphRequest(
      `/sites/${siteId}/lists/Prayer%20Comments/items?$expand=fields`,
      {},
      true
    );
    
    console.log('📋 All prayer comments found:', allCommentsResponse.value?.length || 0);
    
    if (allCommentsResponse.value && allCommentsResponse.value.length > 0) {
      console.log('📋 Detailed comment information:');
      allCommentsResponse.value.forEach((item: any, index: number) => {
        console.log(`\n--- Comment ${index + 1} ---`);
        console.log('ID:', item.id);
        console.log('Title:', item.fields?.Title);
        console.log('CommentText:', item.fields?.CommentText);
        console.log('PrayerId:', item.fields?.PrayerId);
        console.log('PrayerId_x0020_ID:', item.fields?.PrayerId_x0020_ID);
        console.log('PrayerId_x0020_Title:', item.fields?.PrayerId_x0020_Title);
        console.log('Created:', item.fields?.Created);
        console.log('CreatedBy:', item.fields?.CreatedBy);
        console.log('All fields:', Object.keys(item.fields || {}));
        console.log('Field values:', item.fields);
      });
    }
    
    console.log('🔍 === DEBUG PRAYER COMMENTS END ===');
  } catch (error) {
    console.error('❌ Error debugging prayer comments:', error);
  }
};

// Test function to add a comment and immediately fetch it to verify PrayerId is set correctly
export const testPrayerCommentFlow = async (prayerId: number = 2): Promise<void> => {
  try {
    console.log('🧪 === TEST PRAYER COMMENT FLOW START ===');
    console.log('📝 Testing with prayer ID:', prayerId);
    
    // Step 1: Add a test comment
    console.log('📝 Step 1: Adding test comment...');
    const testComment = `Test comment added at ${new Date().toLocaleString()}`;
    const addResult = await addPrayerComment(prayerId, testComment);
    console.log('✅ Comment added successfully:', addResult);
    
    // Step 2: Wait a moment for SharePoint to process
    console.log('⏳ Step 2: Waiting for SharePoint to process...');
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Step 3: Fetch comments for this prayer
    console.log('📝 Step 3: Fetching comments for prayer ID:', prayerId);
    const comments = await getPrayerComments(prayerId);
    console.log('✅ Comments fetched:', comments);
    console.log('📊 Number of comments found:', comments.length);
    
    // Step 4: Debug all comments to see their field values
    console.log('🔍 Step 4: Debugging all comments...');
    await debugPrayerComments();
    
    console.log('🧪 === TEST PRAYER COMMENT FLOW END ===');
  } catch (error) {
    console.error('❌ Error in test prayer comment flow:', error);
  }
};

// Debug function to show raw prayer comments data structure
export const debugPrayerCommentsRaw = async (): Promise<void> => {
  try {
    console.log('🔍 === DEBUG PRAYER COMMENTS RAW DATA ===');
    const authService = getAuthService();
    const sharePointService = getSharePointService(authService);
    
    // Get the site ID
    const siteId = await sharePointService.getSiteId();
    
    // Get all items from Prayer Comments list
    const response = await sharePointService.makeGraphRequest(
      `/sites/${siteId}/lists/Prayer%20Comments/items?$expand=fields`,
      {},
      true
    );
    
    console.log('📋 Raw Prayer Comments list response:', response);
    
    if (response.value && response.value.length > 0) {
      console.log('📋 Found items:', response.value.length);
      
      response.value.forEach((item: any, index: number) => {
        console.log(`\n--- Item ${index + 1} ---`);
        console.log('ID:', item.id);
        console.log('Title:', item.fields?.Title);
        console.log('All field names:', Object.keys(item.fields || {}));
        console.log('All field values:', item.fields);
        
        // Check for any field that might contain prayer ID
        Object.keys(item.fields || {}).forEach(fieldName => {
          const fieldValue = item.fields[fieldName];
          if (fieldName.toLowerCase().includes('prayer') || 
              (typeof fieldValue === 'string' && fieldValue.includes('Prayer')) ||
              (typeof fieldValue === 'number' && fieldValue > 0)) {
            console.log(`🔍 Potential prayer field: ${fieldName} = ${fieldValue}`);
          }
        });
      });
    } else {
      console.log('📋 No items found in Prayer Comments list');
    }
    
    console.log('🔍 === DEBUG PRAYER COMMENTS RAW DATA END ===');
  } catch (error) {
    console.error('❌ Error debugging prayer comments raw data:', error);
  }
};

// Expose all debug functions to window for easy access from browser console
if (typeof window !== 'undefined') {
  (window as any).debugPrayerCommentsFields = debugPrayerCommentsFields;
  (window as any).debugPrayerComments = debugPrayerComments;
  (window as any).debugPrayerCommentsRaw = debugPrayerCommentsRaw;
  (window as any).testPrayerCommentFlow = testPrayerCommentFlow;
}